import { dialog } from "@/dialog";
import { resolveFabledRole } from "@/fabledRole";
import { trustedPromptRole } from "@/nightPrompt";
import { captureGrimoireFrame } from '@/grimoireSnapshot';
import { reconnectDelay } from '../reconnectDelay';
import { DiagnosticsClient } from '../diagnosticsClient';

const profileKey = "clocktower-player-profile";
const connectionKey = "clocktower-live-connection-id";
// 说书人「恢复意图」：只有本页真的坐过某个房间的说书人时才写入，刷新/重连后
// 用它判断“该不该尝试坐回去”。它和 clientId 同源（都是 sessionStorage、同
// 标签页作用域），因此不会跨标签页、跨设备去抢别人的说书人席位。
const storytellerIntentKey = "clocktower-storyteller-intent";

/**
 * 页面启动时「刷新前所在房间」的持久化快照。
 *
 * 之前判断"这次连接是不是恢复已有房间"只看内存里的 `_everConnectedChannel`
 * / `_snapshotRoomId`，硬刷新（F5）后内存必然清空，于是刷新被服务端当成
 * "新建房间"：如果原房间已被销毁，服务端会静默建一个同号空房，页面就停在
 * 「房间号还在、人数 0、圆桌没人」的假死空壳里。
 *
 * 刷新前所在的房间本来就持久化在 localStorage 会话与 URL hash 里，这里在
 * 模块加载时取一次快照，`_open` 据此继续发送 `reconnect=1`，让服务端用
 * tombstone 给出权威结论（房间已销毁 → disbanded），而不是悄悄重建。
 * 只取启动快照，所以用户之后主动创建/加入另一个房间不会被误判成恢复。
 */
const bootResumeRoomId = (() => {
  try {
    const raw = localStorage.getItem("session");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed[1]) return String(parsed[1]);
    }
  } catch (error) {
    /* 存档损坏时按"没有恢复意图"处理 */
  }
  // 注意：这里刻意 **不** 回落到 URL hash。
  // hash 只代表"有人用邀请链接打开了这个房号"，不代表本页此前真的进过这个
  // 房间；把它当成恢复意图，会让"链接指向一个还没被创建的房间"被服务端按
  // 方案B回 disbanded，用户被直接踢出去。真正的"刷新前所在房间"只存在于
  // localStorage 会话里（进入房间时由 persistence 写入），这也是 F5 恢复
  // 依赖的那份存档。
  return "";
})();

// 房间失效原因对应的固定文案（服务端 disbanded 会带 reason + message）。
const ROOM_CLOSED_MESSAGES = {
  manual: "房主已解散房间",
  empty_timeout: "房间因长时间无人在线已自动关闭",
  inactivity_timeout: "房间因长时间无活动已自动解散",
  stale: "该房间已失效，请重新加入或创建房间"
};
/**
 * 取房间关闭提示文案：
 * 1) 已知 reason → 用本表文案（三种销毁原因写法固定，和产品文案一致）；
 * 2) 旧事件/未知 reason → 如果服务端给了 message 就直接用它；
 * 3) 都没有 → 回退到“房主已解散房间”（历史上无 reason 的 disbanded 都是房主解散）。
 */
const roomClosedMessage = (reason, serverMessage = "") => {
  if (reason && ROOM_CLOSED_MESSAGES[reason]) return ROOM_CLOSED_MESSAGES[reason];
  if (serverMessage) return String(serverMessage);
  return ROOM_CLOSED_MESSAGES.manual;
};

// Keep a stable identifier for the lifetime of this browser tab. It survives
// refreshes, but a genuinely different browser receives a different value.
// The room server uses it only to distinguish a reconnect from another person
// attempting to take the reserved owner/storyteller connection.
const getConnectionId = () => {
  let connectionId = sessionStorage.getItem(connectionKey);
  if (connectionId) return connectionId;
  connectionId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(connectionKey, connectionId);
  return connectionId;
};

const getLocalProfile = () => {
  try {
    const profile = JSON.parse(localStorage.getItem(profileKey));
    return {
      name: (profile && profile.id) || "空",
      avatar: (profile && profile.avatar) || ""
    };
  } catch (error) {
    return { name: "空", avatar: "" };
  }
};

// The storyteller token is the authority for role/alignment display. Every
// private send and recovery snapshot must use the exact same precedence as
// that token: an explicitly selected alignment wins; otherwise the printed
// role team supplies the default. Keeping this in one helper prevents a red
// storyteller token from ever being sent as a blue player identity.
const storytellerTokenAlignment = player => {
  if (player && player.alignment === "evil") return "evil";
  if (player && player.alignment === "good") return "good";
  return ["minion", "demon"].includes(player && player.role && player.role.team)
    ? "evil"
    : "good";
};

class LiveSession {
  constructor(store) {
    this.diagnostics = new DiagnosticsClient((type,payload)=>this._send(type,payload));
    this._wss =
      process.env.VUE_APP_LIVE_SOCKET_URL ||
      (process.env.NODE_ENV === "development"
        ? "ws://localhost:8081/"
        : "wss://live.clocktower.online:8080/");
    // this._wss = "ws://localhost:8081/"; // uncomment if using local server with NODE_ENV=development
    this._socket = null;
    this._isSpectator = true;
    this._gamestate = [];
    this._store = store;
    this._pingInterval = 30 * 1000; // 30 seconds between pings
    this._pingTimer = null;
    this._reconnectTimer = null;
    this._reconnectAttempt = 0;
    this._reconnectStartedAt = 0;
    // 只有服务端先发来明确的终止协议（disbanded / kicked /
    // connectionRejected / leftRoom）时，随后到达的 close 才有资格结束房间
    // 会话。单独一个 close（即使 code=1000）只说明传输层断开。
    this._terminalSocketClose = false;
    this._players = {}; // map of players connected to a session
    this._pings = {}; // map of player IDs to ping
    // Role IDs that have already been privately delivered to each player.
    // This lives only in the storyteller's live session: it is deliberately
    // not part of the shared game state, because role delivery is private.
    this._deliveredRoles = {};
    // 说书人提示标记的私密快照：只在本地说书人会话里使用，服务器
    // 只回发给说书人。_applyingReminderNotes 用来避免接收后再次回传。
    this._remoteReminderNotes = null;
    this._pendingReminderNotes = null;
    this._applyingReminderNotes = false;
    this._requestedVoiceRoom = "";
    this._reportedSeatIndex = null;
    this._privateSeatRevision = 0;
    this._storytellerBluffScope = '';
    this._storytellerBluffRecord = null;
    this._storytellerBluffEdit = null;
    this._pendingStorytellerBluffs = null;
    this._applyingBluffDraft = false;
    this._miniGamePending = new Map();
    this._miniGameNoticeTimer = null;
    // 本页是否已经确认过“这个房间真实存在”（收到过权威快照/座位表）。
    // 用来区分“新建房间（服务端还没有任何存档，需要本页发布桌面）”和
    // “重连回一个已经被销毁的房间”——后者绝不能把旧桌面重新发布回去。
    this._confirmedRoomId = "";
    this._staleSnapshotTimer = null;
    // 方案B：只有「这个房间真的收到过权威快照」并且「这是同一个房间的
    // 断线重连」时，才允许 2.5 秒失效启发式参与判断。两个标记都绑定
    // 到具体 roomId，绝不会从 A 房间带到 B 房间。
    this._snapshotRoomId = "";
    this._everConnectedChannel = "";
    this._seatRevision = 0;
    this._channel = "";
    this._authoritativeSeatCount = null;
    // Seat resizing is server-acknowledged rather than a burst of optimistic
    // local pushes. This keeps the storyteller and owner controls identical.
    this._seatResizeRequest = null;
    this._seatResizeTimer = null;
    this._seatChannel = "";
    // Prevent Vuex commits made while applying a server packet from being
    // echoed back as a new storyteller-originated `player` edit.
    this._isApplyingRemoteSeatLayout = false;
    // A full server `gs` snapshot is a restore operation, not a new round of
    // storyteller input. Vuex subscribers are synchronous, so this scoped
    // flag lets the socket subscriber ignore only mutations caused by that
    // restore while preserving the very next real user edit.
    this._isApplyingRemoteGamestate = false;
    // 魂票权威结果（结束唱票自动判定 / 服务端裁定）也要落到说书人本页，
    // 但这条 commit 不能再当成说书人的新编辑回传服务器（否则回显会和
    // 本地编辑互相触发）。Vuex 订阅是同步的，用这个作用域标记只屏蔽
    // 由该包引起的 players/update。
    this._isApplyingRemoteGhostVote = false;
    // 投票结束/重新开始由服务器权威广播给说书人与玩家。接收这些包时
    // 不能再通过 Vuex subscriber 回发成一条新的本地指令。
    this._isApplyingRemoteVoteState = false;
    // 身份发送事务：尚未收到服务器确认的发送（operationId → 操作内容）。
    // 断线/重连时按同一 operationId 安全重试；服务器按它去重。
    this._pendingIdentityOperations = new Map();
    this._pendingAlignmentOperations = new Map();
    this._privateActionPending = new Map();
    this._leavingRoom = false;
    this._leaveRoomTimer = null;
    // 说书人恢复：_restoringStoryteller 表示当前这次 claim 是刷新/重连后的
    // 自动恢复（成功与失败都不打扰用户）；restoreTimer 是回执超时兜底。
    this._restoringStoryteller = false;
    this._storytellerRestoreTimer = null;
    // 说书人席位被别的设备合法占用时，本页只退让一次（退回旁观并留在房间），
    // 不做无限重连、无限 claim。
    this._spectatorFallbackDone = false;
    this._voiceTokenRequestId = 0;
    this._chatRouteRequestId = 0;
    this._voiceMicEntry = null;
    // iOS Safari may freeze both WebSocket and LiveKit while the page is in
    // Picture in Picture / background without delivering `close`.  Remember
    // the lifecycle boundary and replace the transport on resume; the normal
    // identity + seat + voice restore path remains the sole authority.
    this._backgroundedAt = 0;
    this._lastLifecycleResumeAt = 0;
    // 仅同一页面前后台恢复时保留操作意图，不保存/复用旁听凭证，F5不继承。
    this._dayListenLifecycleResume = null;
    this._onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        this._backgroundedAt = Date.now();
      } else {
        this._resumeAfterBackground("visibilitychange");
      }
    };
    this._onPageHide = () => {
      this._backgroundedAt = Date.now();
    };
    this._onPageShow = event => {
      this._resumeAfterBackground(event && event.persisted ? "pageshow-bfcache" : "pageshow");
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this._onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", this._onPageHide);
      window.addEventListener("pageshow", this._onPageShow);
    }
    // reconnect to previous session
    if (this._store.state.session.sessionId) {
      this.connect(this._store.state.session.sessionId);
    }
  }

  /**
   * Open a new session for the passed channel.
   * @param channel
   * @private
   */
  _open(channel) {
    this.disconnect();
    this._terminalSocketClose = false;
    // Tell the server when this is a real reconnect to a room for which this
    // page has already received an authoritative snapshot.  The server can
    // then answer "room no longer exists" explicitly.  A transport delay is
    // merely unknown state and must never be converted into roomInvalid by a
    // client-side stopwatch.
    const reconnectRequested =
      (this._everConnectedChannel === channel && this._snapshotRoomId === channel) ||
      (!!bootResumeRoomId && bootResumeRoomId === channel);
    this._isReconnectForChannel = reconnectRequested;
    // 换房间：上一个房间的说书人恢复意图绝不能带进新房间。
    if (this._channel && this._channel !== channel) {
      this._dayListenLifecycleResume = null;
      this._clearStorytellerIntent();
      this._spectatorFallbackDone = false;
      // 身份也不继承：上一房间的“说书人/已落座”属于上一个房间，新房间默认
      // 以旁观身份进入，只有在新房间里 claim 成功才会成为说书人。
      this._isSpectator = true;
      this._store.commit("session/setSpectator", true);
      this._store.commit("session/setStorytellerProfile", {
        id: "",
        name: "空",
        avatar: ""
      });
    }
    this._channel = channel;
    if (this._seatChannel !== channel) {
      this._seatChannel = channel;
      this._seatRevision = 0;
      this._authoritativeSeatCount = null;
      // 换了房间：上一轮的“已确认房间”记录不适用于新房间。
      this._confirmedRoomId = "";
      this._settleSeatResize(new Error("已切换房间，座位调整已取消"));
    }
    // A realtime-server restart loses its in-memory seat map. The first
    // private game-state packet after reconnect is therefore allowed to
    // restore this browser's own confirmed seat once.
    this._reportedSeatIndex = null;
    this._store.commit("session/setConfirmedSeat", -1);
    const connectionRole = this._store.state.session.isOwner
      ? this._isSpectator
        ? "owner"
        : "owner-storyteller"
      : this._isSpectator
        ? this._store.state.session.playerId
        : "storyteller";
    const socketUrl =
      this._wss +
      channel +
      "/" +
      connectionRole +
      `?clientId=${encodeURIComponent(getConnectionId())}` +
      `&password=${encodeURIComponent(this._store.state.session.roomPassword || "")}` +
      (reconnectRequested ? "&reconnect=1" : "") +
      // 建房时声明的人数：服务端只在“真正新建房间”时校验它落在 Admin 配置的
      // 人数范围内；加入已有房间时这个参数会被服务端忽略。
      (this._store.state.session.requestedSeats > 0
        ? `&seats=${encodeURIComponent(this._store.state.session.requestedSeats)}&offlineGrimoire=${this._store.state.session.offlineGrimoire ? "1" : "0"}`
        : "");
    const socket = new WebSocket(socketUrl);
    this._socket = socket;
    socket.addEventListener("message", event => {
      if (this._socket === socket) this._handleMessage(event);
    });
    socket.onopen = () => {
      if (this._socket === socket) {
        console.info("[ws-lifecycle]", {
          event: this._reconnectAttempt ? "reconnect_success" : "connect_success",
          roomId: channel,
          attempt: this._reconnectAttempt,
          elapsedMs: this._reconnectStartedAt ? Date.now() - this._reconnectStartedAt : 0
        });
        this._onOpen();
        this.diagnostics.start();
      }
    };
    socket.onclose = err => {
      // An older socket can finish closing after its replacement is already
      // open. Its callback must not clear or reconnect over the new socket.
      if (this._socket !== socket) return;
      this.diagnostics.stop();
      this._cancelMiniGames("连接已断开，请恢复连接后重新操作。");
      this._store.commit("session/setMiniGames", null);
      this._store.commit("session/setDayPrivateListen", null);
      this._socket = null;
      clearInterval(this._pingTimer);
      this._pingTimer = null;
      console.info("[ws-lifecycle]", {
        event: "disconnect",
        roomId: channel,
        code: err.code,
        reason: typeof err.reason === "string" ? err.reason : "",
        userInitiated: this._leavingRoom || !this._store.state.session.sessionId,
        terminalAuthorityReceived: this._terminalSocketClose
      });
      if (err.code === 4001) {
        // A refreshed copy of this same browser tab has taken over. Do not
        // start a reconnect loop that would make both copies repeatedly evict
        // one another, and do not erase the shared saved room session.
        // 被接管的旧页面禁止再抢回说书人席位：立刻删掉恢复意图。
        this._clearStorytellerIntent();
        this._restoringStoryteller = false;
        clearTimeout(this._storytellerRestoreTimer);
        this._storytellerRestoreTimer = null;
        this._store.commit("session/setReconnecting", false);
        return;
      }
      if (
        this._terminalSocketClose ||
        this._leavingRoom ||
        !this._store.state.session.sessionId
      ) {
        this._store.commit("session/setReconnecting", false);
        return;
      }
      if (
        err.code === 1000 &&
        typeof err.reason === "string" &&
        (err.reason.includes("已经有说书人") || err.reason.includes("已经有房主")) &&
        !this._spectatorFallbackDone
      ) {
        // 席位已被另一台设备合法占用：只退让一次——删恢复意图、切回旁观，
        // 并以普通玩家身份重连同一个房间，而不是把用户直接踢出房间。
        this._spectatorFallbackDone = true;
        this._clearStorytellerIntent();
        this._restoringStoryteller = false;
        clearTimeout(this._storytellerRestoreTimer);
        this._storytellerRestoreTimer = null;
        // 连 owner 保留角色都被服务端拒绝时，本地 owner 标记必须一起清掉，
        // 否则复制了 localStorage 的页面会显示房主界面却没有真实权限。
        this._store.commit("session/setOwner", false);
        this._store.commit("session/setSpectator", true);
        this._isSpectator = true;
        this._revokeStorytellerPrivateView();
        this._store.commit("session/setStorytellerProfile", {
          id: "",
          name: "空",
          avatar: ""
        });
        this._reconnectTimer = setTimeout(() => this.connect(channel), 800);
        if (!this._store.state.session.roomClosedNotice) {
          dialog.alert(
            "说书人席位已被其他设备占用，你已回到旁观状态（仍然留在房间里）。",
            "席位已被占用"
          );
        }
      } else {
        // 传输层 close 不是房间生命周期事实。1000 + 空 reason、1000 +
        // 未知 reason、1006 等异常断链全部保留 room/session，并向同房间
        // 发起权威 reconnect；房间若已销毁，服务端会用 disbanded+tombstone
        // 明确终止。这样网络抖动不会被客户端自行翻译成“退出房间”。
        this._store.commit("session/setReconnecting", true);
        if (!this._reconnectStartedAt) this._reconnectStartedAt = Date.now();
        this._reconnectAttempt += 1;
        const delayMs = reconnectDelay();
        console.info("[ws-lifecycle]", {
          event: "reconnect_scheduled",
          roomId: channel,
          attempt: this._reconnectAttempt,
          closeCode: err.code,
          closeReason: typeof err.reason === "string" ? err.reason : "",
          delayMs
        });
        this._reconnectTimer = setTimeout(() => this.connect(channel), delayMs);
      }
    };
  }

  /**
   * Send a message through the socket.
   * @param command
   * @param params
   * @private
   */
  _cancelMiniGames(message) {
    this._miniGamePending.forEach(pending => pending.finish({ ok: false, message }));
    clearTimeout(this._miniGameNoticeTimer);
    this._store.commit("session/setMiniGameNotice", "");
    ["rps", "dice"].forEach(name => {
      if (this._store.state.modals[name]) this._store.commit("toggleModal", name);
    });
  }

  requestMiniGame(channel, action, payload = {}) {
    const state = this._store.state.session.miniGames;
    if (!state || state.roomId !== this._channel || !state[channel])
      return Promise.resolve({ ok: false, message: "房间状态尚未就绪。" });
    const requestId = `mini_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
    const request = { ...payload, channel, action, requestId, gameId: payload.gameId !== undefined ? payload.gameId : state.gameId,
      epoch: payload.epoch || state[channel].epoch, groupId: payload.groupId || state[channel].groupId };
    return new Promise(resolve => {
      let retry, timeout;
      const finish = result => {
        clearTimeout(retry); clearTimeout(timeout); this._miniGamePending.delete(requestId); resolve(result);
      };
      this._miniGamePending.set(requestId, { request, finish });
      if (!this._send("miniGame", request)) { finish({ ok: false, message: "连接不可用，提交未发送。" }); return; }
      retry = setTimeout(() => this._send("miniGame", request), 1200);
      timeout = setTimeout(() => finish({ ok: false, message: "未收到服务端确认，请检查公聊和等待状态后重试。" }), 8000);
    });
  }

  _send(command, params, context) {
    if (this._socket && this._socket.readyState === 1) {
      if(command==="chat")this.diagnostics.chatSending(params?.diagnosticId);
      this._socket.send(JSON.stringify(context === undefined ? [command, params] : [command, params, context]));
      return true;
    }
    // Callers that deliver private information must not record a successful
    // delivery while the socket is reconnecting.  They can then retry from
    // the existing buttons instead of silently losing a role/alignment send.
    return false;
  }

  /**
   * Send a message directly to a single playerId, if provided.
   * Otherwise broadcast it.
   * @param playerId player ID or "storyteller", optional
   * @param command
   * @param params
   * @private
   */
  _sendDirect(playerId, command, params) {
    if (playerId) {
      return this._send("direct", { [playerId]: [command, params] });
    } else {
      return this._send(command, params);
    }
  }

  /**
   * Open event handler for socket.
   * @private
   */
  _onOpen() {
    // 同一条连接建立前先判断：本页此前是否已经连过同一个房号。
    // 是 → 这次是断线重连（方案B 允许走 2.5 秒失效启发式）；
    // 否 → 这是首次进入/新建房间（禁止用启发式判房间失效）。
    this._isReconnectForChannel =
      this._isReconnectForChannel || this._everConnectedChannel === this._channel;
    this._everConnectedChannel = this._channel;
    // The room owner connects with the reserved server ID "owner", while a
    // seated owner still has a normal player ID. Register that player ID so
    // private role messages can reach an owner who sits as a player.
    this._send("identity", {
      id: this._store.state.session.playerId,
      profile: getLocalProfile()
    });
    this._restoreLifecycleDayListen();
    // The server immediately returns its public room snapshot after identity
    // registration.  Never ask a storyteller browser for a full table here:
    // that page can be old after a reconnect and must not become a second
    // authority for seats, names or avatars.
    this._ping();
    this._watchForMissingRoomSnapshot();
    // 刷新 / 重连后的说书人恢复：只有本页确实坐过这个房间（恢复意图匹配当前
    // 房号与 clientId）且当前连接仍是旁观身份时才尝试。服务端仍是唯一裁判：
    // 席位空闲才会回 claimed，被别人合法占用则回 occupied —— 本页绝不抢位。
    if (this._isSpectator && this._hasStorytellerIntent()) {
      this._restoringStoryteller = true;
      clearTimeout(this._storytellerRestoreTimer);
      this._storytellerRestoreTimer = setTimeout(() => {
        // 回执超时兜底：不把“恢复中”永久挂住，否则之后真正的 occupied
        // 会被误当成自动恢复而静默掉。
        this._storytellerRestoreTimer = null;
        this._restoringStoryteller = false;
      }, 8000);
      this._send("storyteller");
    }
  }

  /**
   * 断线重连后，如果服务端在短时间内连一份房间快照都发不出来，说明这个
   * 房间已经被销毁（最典型的是 0 人在线 3 分钟后的 empty_timeout），
   * 而页面里还留着销毁前的旧桌面。此时不能再把它当成可用房间，直接进入
   * “房间已失效”状态，让玩家自己返回主页重新加入/创建。
   * 正常情况下（房间还在，或本页正在新建房间）服务端会立刻发 gs/seatLayout。
   */
  _watchForMissingRoomSnapshot() {
    this._cancelStaleSnapshotTimer();
    // `reconnect=1` makes the server decide whether the room still exists.
    // Until either a snapshot or an explicit `disbanded` packet arrives this
    // page remains in reconnecting state.  Absence of a packet is only an
    // unknown network condition, never authoritative proof of deletion.
  }

  _cancelStaleSnapshotTimer() {
    if (!this._staleSnapshotTimer) return;
    clearTimeout(this._staleSnapshotTimer);
    this._staleSnapshotTimer = null;
  }

  /** 服务端确认这个房间真实存在（收到权威快照/座位表）。 */
  _markRoomConfirmed() {
    if (this._channel) this._confirmedRoomId = this._channel;
    // 记录“本页在这个房间上确实收到过权威快照（gs / seatLayout）”。
    this._snapshotRoomId = this._channel;
    this._cancelStaleSnapshotTimer();
    if (this._store.state.session.isReconnecting) {
      console.info("[ws-lifecycle]", {
        event: "reconnect_confirmed",
        roomId: this._channel,
        attempt: this._reconnectAttempt,
        elapsedMs: this._reconnectStartedAt ? Date.now() - this._reconnectStartedAt : 0
      });
    }
    this._store.commit("session/setReconnecting", false);
    this._reconnectAttempt = 0;
    this._reconnectStartedAt = 0;
  }

  /**
   * 聊天红点：只把“真正的新消息”记为未读。
   *
   * 排除三类：
   * 1) 连接/刷新/重连时服务端补发的历史记录；
   * 2) 自己发送的消息（服务端会把公聊原样回发给发送者）；
   * 3) 此刻正打开着聊天并显示在对应分页里的消息。
   *
   * 权限过滤由服务端完成：客户端收不到无权查看的私聊，所以红点不可能
   * 被别人的私聊触发。此处只做本地未读，不广播、不改协议。
   */
  _markChatUnread(message) {
    if (!message || !message.id) return;
    const session = this._store.state.session;
    if (session.chatReadIds?.includes(message.id)) return;
    if (message.senderId && message.senderId === session.playerId) return;
    // 说书人会收到全房私聊作为审计，但只有自己实际参与的会话才算未读。
    // 审计副本不亮会话红点，也不点亮圆桌总红点。
    if (message.kind !== "public") {
      const participants = [message.senderId, ...(message.recipientIds || [])];
      const group = message.kind === "group" && session.chatGroups.find(g => g.id === message.groupId);
      if (!participants.includes(session.playerId) && !(group?.active !== false && group?.memberIds?.includes(session.playerId))) return;
    }
    this._store.commit("session/markChatUnread", message);
  }

  /**
   * Send a ping message with player ID and ST flag.
   * @private
   */
  _ping() {
    this._handlePing();
    this._send("ping", [
      this._isSpectator
        ? this._store.state.session.playerId
        : Object.keys(this._players).length,
      "latency"
    ]);
    clearTimeout(this._pingTimer);
    this._pingTimer = setTimeout(this._ping.bind(this), this._pingInterval);
  }

  /**
   * Handle an incoming socket message.
   * @param data
   * @private
   */
  _handleMessage({ data }) {
    let command, params, context;
    try {
      [command, params, context] = JSON.parse(data);
    } catch (err) {
      console.log("unsupported socket message", data);
    }
    if(this.diagnostics.receive(command,params))return;
    switch (command) {
      case "publishSeatLayout":
        if (!this._store.state.session.isOwner) break;
        // 服务端说“这个房间没有任何存档”。
        //
        // 判据与「方案B」的缺失快照启发式完全一致：只有
        //   ① 本页此前确实收到过这个房号的权威快照（_snapshotRoomId），且
        //   ② 这次连接是同一个房号的真实重连（_isReconnectForChannel），
        // 才有资格把这句话当成“原房间已被销毁”的证据 —— 这时绝不能把旧桌面
        // 重新发布回去（那等于用同一个房号自动重建了旧房间）。
        //
        // 反过来，首次建房 / 首次进入（包括 seatcount:0 的新房初始化、以及
        // 创建后第一次坐上 owner-storyteller 时服务端合法地再次请求座位表）
        // 只是初始化阶段：必须正常发布座位表，绝不能被判成“房间已失效”。
        // Missing-room reconnects are now rejected authoritatively by the
        // server before this connection joins a channel.  Therefore this
        // request is only the legitimate first-room initialization path.
        this.syncSeatCount(true);
        break;
      case "edition":
        this._updateEdition(params);
        break;
      case "editionRejected":
        if (Array.isArray(params?.errors)) dialog.alert(params.errors.join("\n"), "剧本验证失败");
        break;
      case "fabled":
        this._updateFabled(params, context);
        break;
      case "adminNotice":
        // 服务器规则的明确拒绝（座位数超过上限、开局人数低于下限等）。
        // 只是提示，不改变任何本地房间状态；房间仍由既有链路管理。
        if (params && params.message) {
          dialog.alert(params.message, "服务器规则");
        }
        break;
      case "gs": {
        const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
        if (activeGameId && (!params || params.gameId !== activeGameId)) break;
        if (params && Object.prototype.hasOwnProperty.call(params, "gameId"))
          this._store.commit("session/setMarqueeGameScope", params.gameId);
        this._markRoomConfirmed();
        // A seat acknowledgement can arrive before an older full snapshot.
        // Keep the newer, server-confirmed seats instead of allowing that
        // snapshot to put the same person back into their previous chair.
        const incomingSeatRevision = params && params.seatRevision;
        const hasSeatRevision = Number.isInteger(incomingSeatRevision);
        const preserveConfirmedSeats =
          hasSeatRevision && incomingSeatRevision < this._seatRevision;
        if (hasSeatRevision) {
          this._seatRevision = Math.max(this._seatRevision, incomingSeatRevision);
        }
        this._isApplyingRemoteGamestate = true;
        try {
          this._updateGamestate(params, preserveConfirmedSeats);
        } finally {
          // Never leave outbound synchronization disabled if applying one
          // malformed snapshot throws. Real storyteller edits after this
          // packet must continue to reach the server.
          this._isApplyingRemoteGamestate = false;
        }
        break;
      }
      case "player":
        if (
          (this._store.state.session.voiceState.game || {}).id &&
          (!params || params.gameId !== this._store.state.session.voiceState.game.id)
        ) return;
        this._updatePlayer(params);
        break;
      case "identity":
        this._updateIdentity(params);
        break;
      case "privateViewReset":
        if (this._isSpectator && (!this._store.state.session.voiceState.game?.id ||
          params?.gameId === this._store.state.session.voiceState.game.id))
          this._revokeStorytellerPrivateView();
        break;
      case "identityDelivered":
        this._confirmDeliveredIdentities(params);
        break;
      case "roomMode":
        this._store.commit("session/setOfflineGrimoire", params?.offlineGrimoire === true);
        break;
      case "identityDisclosure":
      case "offlineAssignment": {
        const pending = this._privateActionPending.get(params?.requestId);
        if (pending) pending.finish(params);
        break;
      }
      case "identityAlignmentAccepted":
        if (!this._isSpectator && params.gameId === (this._store.state.session.voiceState.game?.id || "")) {
          if (params.seatRevision >= this._seatRevision) this._recordAuthority(params.seats);
          this._pendingAlignmentOperations.delete(params.operationId);
        }
        break;
      case "identityAlignmentRejected":
        if (this._pendingAlignmentOperations.delete(params.operationId)) {
          dialog.alert("发送阵营未提交："+(params.reason === "stale" ? "权威版本已变化，请核对草稿后重新发送。" : "当前局或提交内容已失效。"), "发送阵营失败");
        }
        break;
      case "identityRejected":
        // 服务器明确拒绝了这笔发送（当前只有 stale 一种）：客户端不得
        // 假装成功，必须采纳服务器权威状态并重新计算同步点。
        this._handleIdentityRejected(params);
        break;
      case "identityRecoveryNeeded":
        this._recoverSeatIdentity(params);
        break;
      case "virtualGrimoire":
        this._restoreVirtualGrimoire(params);
        break;
      case "virtualGrimoireSnapshotNeeded":
        this._snapshotVirtualGrimoire();
        break;
      case "reminderNotes":
        // 服务器只把这条消息发给说书人连接；客户端再做一次防御性判断，
        // 确保玩家与旁观者即使收到异常包也不会显示说书人标记。
        if (!this._isSpectator) {
          this._applyReminderNotes(params);
          // 这条包同时是「服务端确认本连接就是说书人」的权威信号：房主以保留
          // 连接角色 owner-storyteller 直接占席、从不经过 claim，只有在这里补写
          // 恢复意图，刷新后才能自动坐回同一个席位。
          this._saveStorytellerIntent();
        }
        break;
      case "roleType":
        this._updateRoleType(params);
        break;
      case "bluffs":
        // 无作用域的旧直发不能覆盖服务器已保存的座位信息。
        break;
      case "bluffEligibility":
        if(!this._isSpectator && params?.gameId === (this._store.state.session.voiceState.game?.id || ''))
          this._store.commit('session/setBluffEligibility',params);
        break;
      case "bluffPublished":
        if(this._pendingBluff?.operationId===params?.operationId) {
          clearTimeout(this._pendingBluff.timer);
          this._store.commit('session/setBluffDeliveryStatus',{state:params.ok?'confirmed':'failed',message:params.message});
          this._pendingBluff=null;
        }
        break;
      case "seatBluffs":
        this._receiveSeatBluffs(params);
        break;
      case "storytellerBluffs":
        this._receiveStorytellerBluffs(params);
        break;
      case "grimoire":
        // 旧融合协议退役，不能再覆盖个人圆桌。
        break;
      case "grimoireSent":
        if (params?.ok === false) dialog.alert(params.error || "魔典未发送，请检查后重试。", "发送失败");
        break;
      case "grimoireSnapshot": {
        const state = this._store.state, snapshot = params?.snapshot;
        if (params?.roomId !== this._channel) break;
        if (params.gameId !== (state.session.voiceState.game?.id || '')) break;
        if (snapshot) {
          if (!this._isSpectator || snapshot.gameId !== state.session.voiceState.game?.id || snapshot.roomId !== this._channel ||
              !this.seatRecipientIds(state.players.players[snapshot.seatIndex] || {}).includes(state.session.playerId)) break;
        }
        this._store.commit("session/setReceivedGrimoire", snapshot || null);
        if (snapshot && params.notify && this._store.state.session.receivedGrimoire?.sequence === snapshot.sequence) {
          const key = `grimoire-notice:${this._channel}:${snapshot.gameId}:${snapshot.seatIndex}:${snapshot.sequence}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            this._store.commit("session/setGrimoireNotice", snapshot.sequence);
          }
        }
        break;
      }
      case "review":
        this._updateSharedGrimoire(params, true);
        break;
      case "seat":
        this._markRoomConfirmed();
        // Accepted seat changes are now server-authoritative and therefore
        // apply to every view, including the claimant and ordinary players.
        // Keep the legacy socket flag in lockstep with the visible player
        // state. A previously interrupted storyteller claim must never make
        // a seated player behave like an unseated observer.
        if (this._store.state.session.isSpectator) this._isSpectator = true;
        if (Number.isInteger(params && params[2])) {
          this._seatRevision = Math.max(this._seatRevision, params[2]);
        }
        this._updateSeat(params, true);
        // An observer may already be subscribed to the public LiveKit room
        // with a listen-only token. Once this seat confirms their identity,
        // request a fresh token even though the room name is still "public";
        // otherwise the microphone button can never publish in day-public.
        if (
          params &&
          params[1] &&
          (params[1].id === this._store.state.session.playerId ||
            (Array.isArray(params[1].coPlayMembers) && params[1].coPlayMembers.some(member =>
              member && member.id === this._store.state.session.playerId
            )))
        )
          this._syncVoiceRoom(true);
        // 身份恢复只由服务器在接受落座时从私密座位档案发给该玩家。
        // 说书人页面收到座位包也不回传任何桌面状态，避免旧页面覆盖
        // 服务器已经确认的新座位与新身份档案。
        break;
      case "seatLayout":
        this._markRoomConfirmed();
        // The realtime service, not a locally persisted browser table, is
        // final authority for occupied chairs. This heals an interrupted move
        // where this page still looked seated while the storyteller correctly
        // saw it standing and therefore could not open a private chat.
        this._applyAuthoritativeSeatLayout(params);
        break;
      case "seatClaim":
        if (params && params.status === "occupied") {
          dialog.alert(
            `${Number(params.index) + 1}号座位已被${params.occupantName || "其他玩家"}占用，请选择其他座位。`,
            "落座失败"
          );
        } else if (params && params.status === "invalid") {
          dialog.alert("该座位已不存在，请等待桌面同步后再选择。", "落座失败");
        }
        break;
      case "leftRoom":
        // The server has removed this browser's seat from the public room.
        // Only now clear the local room, so a deliberate exit can never be
        // mistaken for a refresh and automatically restore the old seat.
        this._terminalSocketClose = true;
        this._finalizeLeaveRoom();
        break;
      case "storyteller":
        this._handleStorytellerSeat(params);
        break;
      case "kicked":
        this._terminalSocketClose = true;
        this._handleRoomClosed("你已被房主移出房间。");
        break;
      case "connectionRejected":
        // 建连阶段的明确、结构化服务端拒绝（密码、封禁、建房规则、客户端
        // 故障）。这与未知网络 close 不同，可以安全停止恢复并清掉会话。
        this._terminalSocketClose = true;
        this._reconnectAttempt = 0;
        this._reconnectStartedAt = 0;
        this._store.commit("session/setReconnecting", false);
        this._store.commit("session/setSessionId", "");
        if (params && params.message && !this._store.state.session.roomClosedNotice) {
          dialog.alert(params.message, "连接失败");
        }
        break;
      case "disbanded":
        // 服务器会带上原因（manual / empty_timeout / inactivity_timeout），
        // 代际失效时服务端也会用同一条消息通知旧连接（reason: "stale"）。
        this._terminalSocketClose = true;
        this._handleRoomClosed(
          roomClosedMessage(params && params.reason, params && params.message),
          (params && params.reason) || "manual"
        );
        break;
      case "ping":
        this._handlePing(params);
        break;
      case "nomination":
        // 兼容两种线格式：旧版直接发数组/ null，新版发带 gameId 的信封。
        {
          const isEnvelope =
            params && typeof params === "object" && !Array.isArray(params);
          const nomination = isEnvelope ? params.nomination : params;
          const voteInstance = isEnvelope ? Number(params.voteInstance) || 0 : 0;
          const inboundGameId = isEnvelope ? String(params.gameId || "") : "";
          const activeGameId =
            (this._store.state.session.voiceState.game || {}).id || "";
          if (activeGameId && inboundGameId && inboundGameId !== activeGameId) return;
          // 只有「白天提名」阶段允许出现唱票画面；阶段之外的提名包一律
          // 丢弃，避免旧广播把圆桌重新推到投票界面。
          if (this._store.state.session.voiceState.phase !== "nomination") return;
          this._isApplyingRemoteVoteState = true;
          try {
            this._store.commit("session/nomination", {
              nomination: nomination || false,
              voteInstance: voteInstance || this._store.state.session.voteInstance
            });
            if (nomination) this._store.commit("session/setVoteRestartContext", null);
          } finally {
            this._isApplyingRemoteVoteState = false;
          }
        }
        break;
      case "voteFinalized":
        if (this._isSpectator) return;
        this._store.commit("session/setVoteRestartContext", params || null);
        break;
      case "swap":
        if (!this._isSpectator) return;
        this._store.commit("players/swap", params);
        break;
      case "move":
        if (!this._isSpectator) return;
        this._store.commit("players/move", params);
        break;
      // 说书人整体重排：只有非说书人连接需要应用（说书人本页已经用同一个
      // permutation 重排过本地顺序），与 swap/move 的既有规则一致。
      case "reorder":
        if (!this._isSpectator) return;
        this._store.commit("players/reorder", params);
        break;
      case "remove":
        if (!this._isSpectator) return;
        this._store.commit("players/remove", params);
        break;
      case "marked":
        if (!this._isSpectator) return;
        {
          // 缺字段/清空包一律按“没有待处决”处理，绝不能变成 1 号位骷髅。
          const marked =
            params && typeof params === "object" ? params.value : params;
          this._store.commit(
            "session/setMarkedPlayer",
            Number.isInteger(marked) && marked >= 0 ? marked : -1
          );
        }
        break;
      case "isNight":
        if (!this._isSpectator) return;
        this._store.commit("toggleNight", params);
        break;
      case "isVoteHistoryAllowed":
        if (!this._isSpectator) return;
        this._store.commit("session/setVoteHistoryAllowed", params);
        if (!params) {
          this._store.commit("session/setVoteHistory", { records: [] });
          if (this._store.state.modals.voteHistory) this._store.commit("toggleModal");
        }
        break;
      case "voteHistory":
        if (!params || params.allowed !== true) {
          this._store.commit("session/setVoteHistory", {
            gameId: params && params.gameId,
            records: []
          });
          return;
        }
        this._store.commit("session/setVoteHistory", params);
        break;
      case "voteHistoryDenied":
        this._store.commit("session/setVoteHistory", {
          gameId: params && params.gameId,
          records: []
        });
        break;
      case "votingSpeed":
        if (!this._isSpectator) return;
        this._store.commit(
          "session/setVotingSpeed",
          params && typeof params === "object" ? params.value : params
        );
        break;
      case "anonymousVote":
        // 匿名投票是服务端权威状态：说书人本地切换后由服务端回显给其它客户端，
        // 普通玩家端据此隐藏真实票数、投票人与当天死人票变化。公开基线
        // （publicVoteless）也只由服务端提供，客户端只读。
        if (!this._isSpectator) return;
        this._store.commit(
          "session/setAnonymousVote",
          params && typeof params === "object" ? params.enabled : params
        );
        this._store.commit(
          "session/setPublicVoteless",
          params && typeof params === "object" ? params.publicVoteless : null
        );
        break;
      case "clearVoteHistory":
        if (!this._isSpectator) return;
        this._store.commit("session/clearVoteHistory");
        break;
      case "isVoteInProgress":
        if (!this._isSpectator) return;
        if (
          params && typeof params === "object" && Number(params.voteInstance) &&
          Number(params.voteInstance) !== Number(this._store.state.session.voteInstance)
        ) return;
        {
          const active = !!(
            params && typeof params === "object" ? params.value : params
          );
          this._store.commit("session/setVoteInProgress", active);
          if (active && typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("townsquare:vote-countdown"));
          }
        }
        break;
      case "timer":
        if (params?.gameId !== undefined && params.gameId !== (this._store.state.session.voiceState.game?.id || '')) break;
        this._isApplyingRemoteGamestate = true;
        try { this._store.commit("session/setDiscussionTimer", params); }
        finally { this._isApplyingRemoteGamestate = false; }
        break;
      case "reviewState":
        if (this._isSpectator)
          this._store.commit("session/setPostGameReview", !!params);
        break;
      case "onlineCount":
        // 房间在线人数由服务端权威广播（连接变化 + 身份注册时补发）。
        // 刷新后不再需要等说书人页面 30 秒一次的 ping 心跳。
        {
          const count = Number(params);
          if (Number.isInteger(count) && count >= 0) {
            this._store.commit("session/setPlayerCount", count);
          }
        }
        break;
      case "vote":
        {
          if (
            params && !Array.isArray(params) && Number(params.voteInstance) &&
            Number(params.voteInstance) !== Number(this._store.state.session.voteInstance)
          ) break;
          const payload = Array.isArray(params)
            ? params
            : params && typeof params === "object"
              ? [params.index, params.vote, params.fromST]
              : null;
          if (payload) this._handleVote(payload);
        }
        break;
      case "lock":
        {
          if (
            params && !Array.isArray(params) && Number(params.voteInstance) &&
            Number(params.voteInstance) !== Number(this._store.state.session.voteInstance)
          ) break;
          const payload = Array.isArray(params)
            ? params
            : params && typeof params === "object"
              ? [params.lock, params.vote]
              : null;
          if (payload) this._handleLock(payload);
        }
        break;
      case "bye":
        this._handleBye(params);
        break;
      case "pronouns":
        this._updatePlayerPronouns(params);
        break;
      case "chat":
        this.diagnostics.chatArrived(params?.id);
        this._store.commit("session/addChatMessage", params);
        this._markChatUnread(params);
        break;
      case "miniGames":
        if (!params || params.roomId !== this._channel) break;
        this._store.commit("session/setMiniGames", params);
        this._miniGamePending.forEach(pending => {
          if (params[pending.request.channel]?.epoch !== pending.request.epoch || params.gameId !== pending.request.gameId)
            pending.finish({ ok: false, message: "流程或配置已变化，请重新操作。" });
        });
        ["rps", "dice"].forEach(name => {
          const canManage = params.canManage && !this._store.state.session.isSpectator;
          if ((!params.canParticipate || (!canManage && !params[name]?.enabled)) && this._store.state.modals[name])
            this._store.commit("toggleModal", name);
        });
        break;
      case "miniGameAccepted": {
        const state = this._store.state.session.miniGames;
        if (!state || params?.gameId !== state.gameId || params.participantKey !== state.selfKey || !["rps", "dice"].includes(params.channel)) break;
        if (params.groupId !== state[params.channel]?.groupId) break;
        if (this._store.state.modals[params.channel]) this._store.commit("toggleModal", params.channel);
        if (params.operatorId !== this._store.state.session.playerId) {
          this._store.commit("session/setMiniGameNotice", "本轮已由共玩伙伴提交。");
          clearTimeout(this._miniGameNoticeTimer);
          this._miniGameNoticeTimer = setTimeout(() => this._store.commit("session/setMiniGameNotice", ""), 3500);
        }
        break;
      }
      case "miniGameResult": {
        const pending = params && this._miniGamePending.get(params.requestId);
        if (pending && params.channel === pending.request.channel && params.epoch === pending.request.epoch) pending.finish(params);
        break;
      }
      case "chatHistory":
        this._store.commit("session/replaceChatHistory", params);
        this._store.commit("session/setChatReadState", params?.readIds || []);
        (params?.messages || []).filter(m => m.gameId === params.gameId && !this._store.state.session.chatReadIds.includes(m.id)).forEach(m => this._markChatUnread(m));
        break;
      case "chatReadState":
        if (params?.gameId === this._store.state.session.voiceState.game?.id || !params?.gameId)
          this._store.commit("session/setChatReadState", params?.ids || []);
        break;
      case "chatPolicy":
        this._store.commit("session/setChatPolicy", params);
        break;
      case "chatError":
        this._store.commit("session/setChatSendError", params?.message || "消息发送失败");
        break;
      case "chatReset":
        this._store.commit("session/resetRoundChat", params);
        break;
      case "chatMode":
        // 房间级设置：服务端是唯一权威，刷新 / 重连 / 开局 / 结束游戏都
        // 由它下发当前真实值，前端只做渲染。
        this._store.commit("session/setChatMode", params && params.mode);
        break;
      case "chatFlight":
        this._store.commit("session/addChatFlight", params);
        break;
      case "interaction":
        this._store.commit("session/addInteraction", params);
        break;
      case "coPlay":
        this._handleCoPlay(params);
        break;
      case "chatGroup":
        if (params && params.action === "remove") {
          this._store.commit("session/removeChatGroup", params);
        } else if (params && params.group) {
          this._store.commit("session/upsertChatGroup", params.group);
        }
        break;
      case "chatRoute":
        if (params && params.requestId === this._chatRouteRequestId &&
            /^(group|direct):/.test(params.target || "")) {
          this._store.commit("session/setChatTarget", params.target);
        }
        break;
      case "quickWakeSnapshot":
        this._store.commit("session/setQuickWakeSnapshot", params || {});
        break;
      case "quickWakeResult":
        this._store.commit("session/setQuickWakeResult", params || null);
        if (params && params.ok) {
          this._store.commit("session/closeQuickWake");
          const failed = (params.results || []).filter(item => !item.ok);
          if (failed.length) {
            dialog.alert(
              `以下座位发送失败：${failed.map(item => `${Number(item.seatIndex) + 1}号（${item.reason || "未知原因"}）`).join("、")}`,
              "部分发送成功"
            );
          }
        } else if (params) {
          dialog.alert(
            (params.results || []).map(item => item.reason).filter(Boolean).join("；") || "发送失败，请检查连接后重试。",
            "快捷唤醒未发送"
          );
        }
        break;
      case "quickWakeReplyResult":
        // 玩家回复的权威回执。过去服务端对回复**完全静默**（只在成功时改任务
        // 状态并广播快照），任何一次校验失败都会让玩家端永久停在「发送中」。
        // 现在无论成功还是失败都有回执：组件据此解除 sending，失败时给出中文原因。
        this._store.commit("session/setQuickWakeResult", params || null);
        if (params && params.ok === false && params.reason) {
          dialog.alert(params.reason, "快捷唤醒回复未成功");
        }
        break;
      case "storytellerProfile":
        this._store.commit("session/setStorytellerProfile", params);
        break;
      case "voiceState":
        {
          const oldPhase = this._store.state.session.voiceState.phase;
          const oldGameId=this._store.state.session.voiceState.game?.id||'';
          this._isApplyingRemoteGamestate = true;
          try {
            this._store.commit("session/setVoiceState", params);
            if(oldGameId!==(params?.game?.id||'')) {
              this._resetBluffDraft();
              this._store.commit('session/setBluffEligibility',null);
              this._store.commit('session/setBluffDeliveryStatus',{state:'',message:''});
              if(this._pendingBluff)clearTimeout(this._pendingBluff.timer);
              this._pendingBluff=null;this._retryBluff=null;
            }
            const phase = this._store.state.session.voiceState.phase;
            this._store.commit("toggleNight", phase === "night");
            if (phase !== oldPhase && phase !== "nomination") {
              this._store.commit("session/clearPendingDayResolution");
            }
            if (phase !== oldPhase && phase === "night") {
              this._store.commit("session/setMarkedPlayer", -1);
            }
          } finally {
            this._isApplyingRemoteGamestate = false;
          }
        }
        // Chair mode, all-muted and a newly occupied seat change the token's
        // canPublish grant without necessarily changing the room ID.
        this._syncVoiceRoom(true);
        if(!this._isSpectator)this._send('storytellerBluffs');
        if(this._isSpectator && this._reportedSeatIndex!==null && this._bluffSyncGame!==params?.game?.id) {
          this._bluffSyncGame=params?.game?.id;
          this._receivedBluffVersion=0;this._store.commit('players/setBluff');
          this._send('seatPrivateSync');
        }
        break;
      case "gameReset":
        // 服务端确认“新一局已经开始/本局已作废”。本地缓存的私密身份、
        // 身份正反面与授权魔典都属于上一局，必须一起清掉；这里不做任何
        // 弹窗，只有说书人的按钮状态会随 voiceState 一起更新。
        {
          const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
          const resetGameId = String((params && params.gameId) || "");
          if (activeGameId && resetGameId && activeGameId !== resetGameId) break;
          if (activeGameId && !resetGameId && params && params.reason !== "game") break;
          this._clearGameScopedState();
        }
        break;
      case "gameResult":
        this._store.commit("session/setGameResult", params);
        // 结束后进入复盘：说书人页面切回白天，其它浏览器跟随 isNight 广播；
        // 说书人的主席麦/自由模式/禁言等控制能力完全不受影响。
        if (!this._isSpectator && this._store.state.grimoire.isNight) {
          this._store.commit("setNight", false);
        }
        // 复盘自动开麦：服务端只在刚结束的窗口内、且该用户仍有公聊发言
        // 权限时才把 autoStartMic 置为 true，因此不会绕过控麦。
        this.requestVoiceAction({ action: "token", roomId: "public", autoStartMic: true });
        // 游戏结束即自动复盘：继续复用已有的私密 direct 分发与 reviewState
        // 授权链，不把身份或提示标记塞进公共 gs / seatLayout。
        if (!this._isSpectator) {
          this.publishPostGameReview(true);
          this._store.commit("session/setPostGameReview", true);
        }
        break;
      case "dayPrivateListen": {
        const credentials = params && params.credentials;
        // URL 固定走当前业务 Socket 的旁听门禁，不接收可任意指定的媒体地址。
        const packet = { ...params, credentials: credentials ? {
          ...credentials, url: new URL("__day-listen", this._wss.endsWith("/") ? this._wss : this._wss + "/").href
        } : null };
        this._store.commit("session/setDayPrivateListen", packet);
        break;
      }
      case "voiceToken":
        if (!params || (Number.isInteger(params.voiceRequestId) && params.voiceRequestId !== this._voiceTokenRequestId)) break;
        if (this._requestedVoiceRoom && params.roomId !== this._requestedVoiceRoom) break;
        this._store.commit("session/setVoiceToken", params);
        this._store.commit("session/setPublicVoiceToken", params && params.publicListen || null);
        break;
      case "voiceSpeaking":
        this._store.commit("session/setVoiceSpeaking", params);
        break;
      case "voiceRequest":
        this._handleVoiceRequest(params);
        break;
      case "bootlegger":
        if (this._isSpectator) {
          this._store.commit("session/setBootlegger", params);
          this._store.commit("players/applyBootlegger", params);
        }
        break;
      case "useOldOrder":
        if (this._isSpectator) {
          this._store.commit("session/setUseOldOrder", params);
          this._store.commit("setEdition", this._store.state.edition);
          this._store.commit("refreshLegacyRoleDefinitions");
        }
        break;
      case "useOldRole":
        if (this._isSpectator) {
          this._store.commit("session/setUseOldRole", params);
          this._store.commit("setEdition", this._store.state.edition);
          this._store.commit("refreshLegacyRoleDefinitions");
        }
        break;
    }
  }

  /**
   * Connect to a new live session, either as host or spectator.
   * Set a unique playerId if there isn't one yet.
   * @param channel
   */
  connect(channel) {
    if (!this._store.state.session.playerId) {
      this._store.commit(
        "session/setPlayerId",
        Math.random()
          .toString(36)
          .substr(2)
      );
    }
    this._pings = {};
    this._store.commit("session/setPlayerCount", 0);
    this._store.commit("session/setPing", 0);
    this._isSpectator = this._store.state.session.isSpectator;
    this._open(channel);
  }

  /**
   * Close the current session, if any.
   */
  disconnect() {
    this.diagnostics.stop();
    this._reviewLoadGeneration = (this._reviewLoadGeneration || 0) + 1;
    this._reviewImageProbes?.forEach(cancel => cancel());
    this._cancelMiniGames("已离开或正在重新连接房间。");
    this._store.commit("session/setMiniGames", null);
    clearTimeout(this._leaveRoomTimer);
    this._cancelStaleSnapshotTimer();
    this._leaveRoomTimer = null;
    this._leavingRoom = false;
    this._pings = {};
    this._store.commit("session/setPlayerCount", 0);
    this._store.commit("session/setPing", 0);
    // `_open()` also uses this cleanup before a reconnect attempt. Keep the
    // visible reconnect state until a server snapshot confirms the room.
    if (!this._reconnectAttempt) {
      this._store.commit("session/setReconnecting", false);
    }
    clearTimeout(this._reconnectTimer);
    if (this._socket) {
      if (this._isSpectator) {
        this._sendDirect(
          "storyteller",
          "bye",
          this._store.state.session.playerId
        );
      }
      this._socket.close(1000);
      this._socket = null;
    }
  }

  /**
   * Publish the current gamestate.
   * Optional param to reduce traffic. (send only player data)
   * @param playerId
   * @param isLightweight
   */
  sendGamestate(playerId = "", isLightweight = false) {
    if (this._isSpectator) return;
    this._gamestate = this._store.state.players.players.map(player => ({
      name: player.name,
      id: player.id,
      avatar: player.avatar,
      votes:
        player.votes === undefined || player.votes === null
          ? 1
          : Math.max(0, Number(player.votes) || 0),
      isDead: player.isDead,
      isVoteless: player.isVoteless,
      pronouns: player.pronouns
    }));
    if (isLightweight) {
      this._sendDirect(playerId, "gs", {
        gameId: (this._store.state.session.voiceState.game || {}).id || "",
        gamestate: this._gamestate,
        seatCount: this._gamestate.length,
        storytellerProfile: this._store.state.session.storytellerProfile,
        seatRevision: this._seatRevision,
        isLightweight
      });
    } else {
      const { session, grimoire } = this._store.state;
      const { fabled } = this._store.state.players;
      this.sendEdition(playerId);
      this._sendDirect(playerId, "gs", {
        gameId: (session.voiceState.game || {}).id || "",
        gamestate: this._gamestate,
        seatCount: this._gamestate.length,
        storytellerProfile: session.storytellerProfile,
        seatRevision: this._seatRevision,
        isNight: grimoire.isNight,
        isVoteHistoryAllowed: session.isVoteHistoryAllowed,
        anonymousVote: session.anonymousVote,
        publicVoteless: session.publicVoteless,
        nomination: session.nomination,
        votingSpeed: session.votingSpeed,
        lockedVote: session.lockedVote,
        isVoteInProgress: session.isVoteInProgress,
        markedPlayer: session.markedPlayer,
        dayNumber: session.dayNumber,
        dayHistory: session.dayHistory,
        pendingDayResolution: session.pendingDayResolution,
        discussionTimer: session.discussionTimer,
        bootlegger: session.bootlegger,
        isUseOldOrder: session.isUseOldOrder,
        isUseOldRole: session.isUseOldRole,
        fabled: fabled.map(f => (f.isCustom ? f : { id: f.id })),
        ...(session.nomination ? { votes: session.votes } : {})
      });
    }
  }

  /**
   * Update the gamestate based on incoming data.
   * @param data
   * @private
   */
  _updateGamestate(data, preserveConfirmedSeats = false) {
    const {
      gamestate,
      isLightweight,
      isNight,
      isVoteHistoryAllowed,
      voteHistory,
      nomination,
      voteInstance,
      voteRestartContext,
      votingSpeed,
      votes,
      lockedVote,
      isVoteInProgress,
      anonymousVote,
      publicVoteless,
      markedPlayer,
      fabled,
      dayNumber,
      dayHistory,
      pendingDayResolution,
      discussionTimer,
      storytellerProfile,
      bootlegger,
      isUseOldOrder,
      isUseOldRole
    } = data;
    const players = this._store.state.players.players;
    // A stale full snapshot may still contain an old seat layout. Its phase
    // and timer remain useful, but its occupants must not overwrite a newer
    // authoritative seat acknowledgement.
    if (this._isSpectator && !preserveConfirmedSeats) {
      // adjust number of players
      if (players.length < gamestate.length) {
        this._store.commit("players/addSeats", gamestate.length - players.length);
      } else if (players.length > gamestate.length) {
        for (let x = players.length; x > gamestate.length; x--) {
          this._store.commit("players/remove", x - 1);
        }
      }
      // update status for each player
      gamestate.forEach((state, x) => {
        const player = players[x];
        const { roleId } = state;
        // update relevant properties
        ["name", "id", "avatar", "votes", "isDead", "isVoteless", "pronouns", "coPlayMembers"].forEach(property => {
          const value = state[property];
          if (player[property] !== value) {
            this._store.commit("players/update", { player, property, value });
          }
        });
        // Public state packets intentionally omit roles. Only a direct or
        // legacy packet that explicitly contains roleId may change one.
        if (
          Object.prototype.hasOwnProperty.call(state, "roleId") &&
          roleId &&
          player.role.id !== roleId
        ) {
          const role =
            this._store.state.roles.get(roleId) ||
            this._store.getters.rolesJSONbyId.get(roleId);
          if (role) {
            this._store.commit("players/update", {
              player,
              property: "role",
              value: role
            });
          }
        } else if (
          Object.prototype.hasOwnProperty.call(state, "roleId") &&
          !roleId &&
          player.role.team === "traveler"
        ) {
          this._store.commit("players/update", {
            player,
            property: "role",
            value: {}
          });
        }
      });
    }
    if (storytellerProfile) {
      this._store.commit("session/setStorytellerProfile", storytellerProfile);
    }
    if (!isLightweight) {
      this._store.commit("toggleNight", !!isNight);
      this._store.commit("session/setVoteHistoryAllowed", isVoteHistoryAllowed);
      this._store.commit("session/setVoteHistory", {
        gameId: data.gameId || "",
        records: Array.isArray(voteHistory) ? voteHistory : []
      });
      this._store.commit("session/setAnonymousVote", anonymousVote === true);
      this._store.commit("session/setPublicVoteless", publicVoteless || null);
      // A snapshot that still carries an old nomination must not drag the
      // room back onto the vote screen once the storyteller has moved the
      // table out of 「白天提名」. The phase wins over the stale payload.
      const isNominationPhase =
        this._store.state.session.voiceState.phase === "nomination";
      this._store.commit("session/nomination", {
        nomination: isNominationPhase ? nomination : false,
        voteInstance: Number(voteInstance) || 0,
        votes: isNominationPhase ? votes : [],
        votingSpeed,
        lockedVote: isNominationPhase ? lockedVote : 0,
        isVoteInProgress: isNominationPhase ? isVoteInProgress : false
      });
      this._store.commit(
        "session/setVoteRestartContext",
        !this._isSpectator && isNominationPhase && !nomination
          ? voteRestartContext || null
          : null
      );
      // 快照缺字段时按“没有待处决”处理；绝不能把 undefined 写进本地
      // 状态（它会被回传成 null，又被服务器当成 0 号座位）。
      this._store.commit(
        "session/setMarkedPlayer",
        Number.isInteger(markedPlayer) && markedPlayer >= 0 ? markedPlayer : -1
      );
      this._store.commit("session/setDayState", {
        dayNumber,
        dayHistory,
        pendingDayResolution
      });
      this._store.commit("session/setDiscussionTimer", discussionTimer);
      this._store.commit("session/setBootlegger", bootlegger);
      this._store.commit("session/setUseOldOrder", isUseOldOrder);
      this._store.commit("session/setUseOldRole", isUseOldRole);
      this._store.commit("setEdition", this._store.state.edition);
      this._store.commit("refreshLegacyRoleDefinitions");
      this._store.commit("players/setFabled", {
        fabled: (fabled || []).map(f => resolveFabledRole(f, this._store.state.fabled))
      });
      this._store.commit("cacheRoomFabledDefinitions", this._store.state.players.fabled);
      this._store.commit("players/applyBootlegger", bootlegger);
    }
    // 观战者落座前申请公共语音会被服务器拒绝。落座状态同步完成后
    // 主动重试一次令牌，避免必须刷新页面才能真正进入语音频道。
    const selfSeatIndex = players.findIndex(player =>
      (player.id && player.id === this._store.state.session.playerId) ||
      (Array.isArray(player.coPlayMembers) && player.coPlayMembers.some(member =>
        member && member.id === this._store.state.session.playerId
      ))
    );
    const selfIsSeated = selfSeatIndex >= 0;
    // The storyteller sends a joining player its state privately. That packet
    // does not pass through the server's public `gs` handler, so after a
    // server restart the server otherwise has no way to know this player is
    // already seated and gives them a listen-only voice token forever.
    if (
      this._isSpectator &&
      selfIsSeated &&
      this._reportedSeatIndex !== selfSeatIndex
    ) {
      const player = players[selfSeatIndex];
      // 共玩重连补claim只能回显自己的权威名称，不能把主成员名写给副成员。
      const member = (player.coPlayMembers || []).find(item =>
        item && item.id === this._store.state.session.playerId
      );
      this._reportedSeatIndex = selfSeatIndex;
      this._send("claim", [
        selfSeatIndex,
        {
          id: this._store.state.session.playerId,
          name: (member ? member.name : player.name) || getLocalProfile().name || "空",
          avatar: player.avatar || getLocalProfile().avatar || ""
        }
      ]);
    }
    if (!selfIsSeated) this._reportedSeatIndex = null;
    if (selfIsSeated && !this._store.state.session.voiceToken) {
      this._requestedVoiceRoom = "";
      this._syncVoiceRoom();
    }
  }

  /**
   * Explicit menu exit. Refresh/temporary connection loss intentionally do
   * not call this method, allowing those sessions to restore their seat.
   */
  requestLeaveRoom() {
    if (this._leavingRoom) return;
    this._leavingRoom = true;
    const sent = this._send("leave", {
      id: this._store.state.session.playerId
    });
    if (!sent) {
      this._finalizeLeaveRoom();
      return;
    }
    // Do not strand the user if the connection dies exactly while leaving.
    // A normal exit is acknowledged by `leftRoom` almost immediately.
    this._leaveRoomTimer = setTimeout(() => this._finalizeLeaveRoom(), 1200);
  }

  _finalizeLeaveRoom() {
    clearTimeout(this._leaveRoomTimer);
    this._leaveRoomTimer = null;
    if (!this._leavingRoom && !this._store.state.session.sessionId) return;
    this._leavingRoom = false;
    // 离开房间 = 恢复意图作废（主动退出、解散、超时、代际失效都走这里或
    // _handleRoomClosed），下次进入房间绝不会自动坐回说书人。
    this._clearStorytellerIntent();
    this._restoringStoryteller = false;
    clearTimeout(this._storytellerRestoreTimer);
    this._storytellerRestoreTimer = null;
    this._spectatorFallbackDone = false;
    this._reconnectAttempt = 0;
    this._reconnectStartedAt = 0;
    // 离开房间时清掉方案B 的两个房间级标记，避免 A 房间的状态带到 B 房间。
    this._snapshotRoomId = "";
    this._everConnectedChannel = "";
    this._isReconnectForChannel = false;
    this._store.commit("session/leaveRoom");
  }

  /**
   * Publish an edition update. ST only
   * @param playerId
   */
  sendEdition(playerId = "") {
    if (this._isSpectator) return;
    const { edition } = this._store.state;
    let roles;
    if (!edition.isOfficial) {
      roles = this._store.getters.customRolesStripped;
    }
    /*
     * 夜间多人语音的「全体爪牙」必须在服务端按角色类型筛选，因此房间权威
     * 剧本除了 edition 之外还要带一份最小的「角色 id → 阵营(team)」映射。
     * 官方剧本过去只发 { id }，服务端拿不到角色类型；这份映射与 customRoles
     * 一样属于剧本元数据，不含任何玩家身份。
     */
    const roleTeams = {};
    if (this._store.state.roles && typeof this._store.state.roles.forEach === "function") {
      this._store.state.roles.forEach((role, id) => {
        if (role && id && role.team) roleTeams[id] = String(role.team);
      });
    }
    this._sendDirect(playerId, "edition", {
      edition: edition.isOfficial ? { id: edition.id } : edition,
      roleTeams,
      fabledContext: this._store.state.fabledContext,
      fabled: this._store.state.players.fabled.map(f => (f.isCustom ? f : { id: f.id })),
      // 定义与启用状态分离：删掉本局标记后，刷新仍可从当前剧本目录重加。
      fabledDefinitions: (edition.fabledRoles || []).map(id => this._store.state.fabled.get(id)).filter(Boolean),
      ...(roles ? { roles } : {})
    });
  }

  /**
   * Update edition and roles for custom editions.
   * @param edition
   * @param roles
   * @private
   */
  _updateEdition({ edition, roles, fabled, fabledContext, fabledDefinitions }) {
    this._store.commit("setFabledContext", fabledContext || "");
    if (!this._isSpectator) return;
    this._store.commit("setEdition", edition);
    if (roles) {
      this._store.commit("setCustomRoles", roles);
      if (this._store.state.roles.size !== roles.length) {
        const missing = [];
        roles.forEach(({ id }) => {
          if (!this._store.state.roles.get(id)) {
            missing.push(id);
          }
        });
        dialog.alert(
          `该房间包含本地未找到的自定义角色，请先加载对应剧本。缺少角色：${missing.join(", ")}`,
          "缺少自定义角色"
        );
        this.disconnect();
        this._store.commit("toggleModal", "edition");
      }
    }
    this._store.commit("cacheRoomFabledDefinitions", Array.isArray(fabledDefinitions) ? fabledDefinitions : []);
    if (Array.isArray(fabled)) this._updateFabled(fabled, fabledContext || "");
    // 素材晚到时按服务端座位档案恢复，不用空角色对象永久替代已披露身份。
    if(this._reportedSeatIndex!==null)this._send('seatPrivateSync');
  }

  /**
   * Publish a fabled update. ST only
   */
  sendFabled() {
    if (this._isSpectator) return;
    const { fabled } = this._store.state.players;
    this._send(
      "fabled",
      fabled.map(f => (f.isCustom ? f : { id: f.id })),
      this._store.state.fabledContext
    );
  }

  /**
   * Update fabled roles.
   * @param fabled
   * @private
   */
  _updateFabled(fabled, context = "") {
    if (!this._isSpectator) return;
    if (context !== this._store.state.fabledContext) return;
    if (!Array.isArray(fabled)) return;
    this._store.commit("players/setFabled", {
      // Admin 新增的 Fabled 由说书人连同完整定义一起下发，房间内直接使用那份
      // 快照：后台之后发布新版本或停用它，都不会影响正在进行的房间。
      fabled: fabled.map(f =>
        resolveFabledRole(f, this._store.state.fabled)
      )
    });
    this._store.commit("cacheRoomFabledDefinitions", this._store.state.players.fabled);
    this._store.commit("players/applyBootlegger", this._store.state.session.bootlegger);
  }

  /**
   * Publish a player update.
   * @param player
   * @param property
   * @param value
   */
  sendPlayer({ player, property, value }) {
    if (this._isSpectator || property === "reminders" || property === "alignment") return;
    const index = this._store.state.players.players.indexOf(player);
    if (property === "role") {
      this._store.commit("session/setPrivateRoleType", {
        playerId: player.id,
        team: ""
      });
      // 空白座位可以在线下魔典中先获得身份；此时尚未发送过完整游戏状态，
      // 因而同步缓存可能还没有对应位置。
      // Every role is private, including travelers. distributeRoles() is the
      // only route that sends it, directly to the matching seated player.
    } else {
      this._send("player", {
        gameId: (this._store.state.session.voiceState.game || {}).id || "",
        index,
        property,
        value
      });
    }
  }

  /**
   * Update a player based on incoming data. Player only.
   * @param index
   * @param property
   * @param value
   * @private
   */
  _updatePlayer({ index, property, value }) {
    // 说书人本页对自己的编辑是乐观更新，服务端回显不需要再套一遍；
    // 但魂票的「结束唱票自动判定」和人工裁定都由服务端裁决，唯一权威
    // 结果也是服务端广播的 isVoteless。若这里一并丢弃，说书人点完
    // 「结束唱票」后圆桌图标会一直停在旧状态，直到刷新才变。
    const authoritativeGhostVote = !this._isSpectator && property === "isVoteless";
    if (!this._isSpectator && !authoritativeGhostVote) return;
    const player = this._store.state.players.players[index];
    if (!player) return;
    // special case where a player stops being a traveler
    if (property === "role") {
      if (!value && player.role.team === "traveler") {
        // reset to an unknown role
        this._store.commit("players/update", {
          player,
          property: "role",
          value: {}
        });
      } else {
        // load role, first from session, the global, then fail gracefully
        const role =
          this._store.state.roles.get(value) ||
          this._store.getters.rolesJSONbyId.get(value) ||
          {};
        this._store.commit("players/update", {
          player,
          property: "role",
          value: role
        });
      }
    } else {
      // just update the player otherwise
      if (authoritativeGhostVote) this._isApplyingRemoteGhostVote = true;
      try {
        this._store.commit("players/update", { player, property, value });
      } finally {
        this._isApplyingRemoteGhostVote = false;
      }
    }
  }

  /**
   * Publish a player pronouns update
   * @param player
   * @param value
   * @param isFromSockets
   */
  sendPlayerPronouns({ player, value, isFromSockets }) {
    //send pronoun only for the seated player or storyteller
    //Do not re-send pronoun data for an update that was recieved from the sockets layer
    if (
      isFromSockets ||
      (this._isSpectator && this._store.state.session.playerId !== player.id)
    )
      return;
    const index = this._store.state.players.players.indexOf(player);
    this._send("pronouns", [index, value]);
  }

  /**
   * Update a pronouns based on incoming data.
   * @param index
   * @param value
   * @private
   */
  _updatePlayerPronouns([index, value]) {
    const player = this._store.state.players.players[index];

    this._store.commit("players/update", {
      player,
      property: "pronouns",
      value,
      isFromSockets: true
    });
  }

  /**
   * Handle a ping message by another player / storyteller
   * @param playerIdOrCount
   * @param latency
   * @private
   */
  _handlePing([playerIdOrCount = 0, latency] = []) {
    const now = new Date().getTime();
    if (!this._isSpectator) {
      // remove players that haven't sent a ping in twice the timespan
      for (let player in this._players) {
        if (now - this._players[player] > this._pingInterval * 2) {
          delete this._players[player];
          delete this._pings[player];
        }
      }
      // Seat occupancy is owned by the realtime server, not by this legacy
      // client-local ping list. A player can legitimately have claimed a
      // seat before this storyteller tab receives their first heartbeat (or
      // while this tab is reconnecting). Clearing IDs here made a newly
      // accepted first seat visibly “stand up” again a moment later.
      // Keep `_players` only for latency/player-count display.
      // store new player data
      if (playerIdOrCount) {
        this._players[playerIdOrCount] = now;
        const ping = parseInt(latency, 10);
        if (ping && ping > 0 && ping < 30 * 1000) {
          // ping to Players
          this._pings[playerIdOrCount] = ping;
          const pings = Object.values(this._pings);
          this._store.commit(
            "session/setPing",
            Math.round(pings.reduce((a, b) => a + b, 0) / pings.length)
          );
        }
      }
    } else if (latency) {
      // ping to ST
      this._store.commit("session/setPing", parseInt(latency, 10));
    }
    /*
     * 在线人数不再由这条 ping 心跳推导，只用服务端权威的 `onlineCount` 包
     * （见 case "onlineCount"）。
     *
     * 旧实现的历史问题：
     *   1) ping 载荷的第一个元素是复用的（说书人发「人数」，旁观者发
     *      「自己的 playerId」），接收端猜错就会把随机 ID 当人数；
     *   2) 即使猜对，说书人本地的 `_players` 也是靠心跳慢慢长出来的——
     *      玩家刷新后立刻是 0，只能等说书人下一次 30 秒心跳，说书人标签页
     *      被后台节流时更久，正是"刷新后在线人数显示 0"的来源；
     *   3) 说书人自己刷新后 `_players` 为空，会把 0 广播给全房。
     * `_players` 仍保留用于延迟（ping）显示，不参与人数。
     */
  }

  /**
   * Handle a player leaving the sessions. ST only
   * @param playerId
   * @private
   */
  _handleBye(playerId) {
    if (this._isSpectator) return;
    delete this._players[playerId];
    // 人数只由服务端权威 onlineCount 广播决定；这里不再用本地心跳名单
    // 推导人数，否则说书人本地名单不完整时会把错误的 0 写进徽标。
  }

  /**
   * Claim a seat, needs to be confirmed by the Storyteller.
   * Seats already occupied can't be claimed.
   * @param seat either -1 to vacate or the index of the seat claimed
   */
  claimSeat(seat) {
    // The persisted UI flag and the private socket flag can briefly disagree
    // during a reconnect. Only block a real storyteller; an observer with a
    // stale UI flag still needs to be able to retry their server-side claim.
    if (!this._isSpectator && !this._store.state.session.isSpectator) return;
    this._isSpectator = true;
    const players = this._store.state.players.players;
    if (players.length > seat && (seat < 0 || !players[seat].id)) {
      const profile = {
        id: this._store.state.session.playerId,
        ...getLocalProfile()
      };
      // Owners and ordinary players use the same server-authoritative claim
      // path. Otherwise an owner with a stale view could still overwrite a
      // seat that another browser claimed milliseconds earlier.
      this._send("claim", [seat, profile]);
    }
  }

  /**
   * Receive a private role + alignment change. This command is delivered
   * directly by the storyteller, never through the public room state.
   */
  _updateIdentity({ gameId, index, playerId, roleId, alignment, notify, seatRevision } = {}) {
    if (!this._isSpectator) return;
    const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
    if (activeGameId && gameId !== activeGameId) return;
    if (Number.isInteger(seatRevision) && seatRevision < this._privateSeatRevision) return;
    const ownId = this._store.state.session.playerId;
    const player = this._store.state.players.players[index];
    if (playerId !== ownId || !player ||
      (player.id !== ownId && !(player.coPlayMembers || []).some(member => member.id === ownId))) return;
    const role =
      this._store.state.roles.get(roleId) ||
      this._store.getters.rolesJSONbyId.get(roleId) ||
      {};
    this._store.commit("players/setIdentity", {
      player,
      role,
      alignment
    });
    this._store.commit("session/setPrivateIdentity", {
      playerId: this._store.state.session.playerId,
      roleId,
      alignment,
      seatIndex: index
    });
    // 新身份包本身也携带阵营；覆盖旧的“仅阵营”缓存，避免后续角色
    // 更新仍沿用上一轮的呼吸环颜色。
    this._store.commit("session/setPrivateRoleAlignment", {
      playerId: this._store.state.session.playerId,
      alignment
    });
    this._store.commit("session/setPrivateRoleType", {
      playerId: this._store.state.session.playerId,
      team: ""
    });
    // The change is explicitly announced to this player, so reveal the new
    // private token immediately instead of leaving an old local hide setting
    // to make it look as if the send failed.
    this._store.commit("session/revealOwnRole");
    if (notify) dialog.alert("你的身份信息已改变，请查看。", "身份已更新");
  }

  _confirmDeliveredIdentities({ seats, operationId } = {}) {
    if (this._isSpectator || !Array.isArray(seats)) return;
    const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
    let confirmedCurrentGame = false;
    seats.forEach(identity => {
      if (!identity || identity.gameId !== activeGameId) return;
      const index = Number(identity.index);
      if (!Number.isInteger(index) || index < 0) return;
      confirmedCurrentGame = true;
      this._recordAuthority([{ ...identity, index }]);
      if (identity.disclosure === "alignment") return;
      this._store.commit("session/setStorytellerDeliveredIdentity", {
        index,
        roleId: identity.roleId || "",
        alignment: identity.alignment,
        identityVersion: identity.identityVersion
      });
      const player = this._store.state.players.players[index];
      if (player) {
        this.seatRecipientIds(player).forEach(playerId => {
          this._deliveredRoles[playerId] = `${identity.roleId || ""}|${
            identity.alignment === "evil" ? "evil" : "good"
          }`;
        });
      }
    });
    // 只有确认的是「当前局」的回执才能结清 pending：上一局的迟到 ACK 既
    // 不能把新局同步点变绿，也不能确认新局里更新的那一笔发送。
    if (operationId && confirmedCurrentGame) this._forgetPendingIdentity(operationId);
  }

  /**
   * Resume a suspended mobile browser without treating it as an explicit
   * leave.  A socket can still report OPEN after iOS has silently frozen the
   * underlying network, so a meaningful background interval replaces it even
   * when readyState has not changed.  `connect()` reuses the existing room,
   * player id, client id and storyteller intent; it never creates a room or
   * changes generation on its own.
   */
  _resumeAfterBackground(source) {
    const now = Date.now();
    const backgroundedFor = this._backgroundedAt ? now - this._backgroundedAt : 0;
    const restoredFromBfcache = source === "pageshow-bfcache";
    const socketUnusable = !this._socket || this._socket.readyState !== WebSocket.OPEN;
    if (
      !this._channel ||
      !this._store.state.session.sessionId ||
      this._leavingRoom ||
      this._store.state.session.roomClosedNotice ||
      (!restoredFromBfcache && !socketUnusable && backgroundedFor < 1500) ||
      now - this._lastLifecycleResumeAt < 750
    ) {
      return;
    }
    this._backgroundedAt = 0;
    this._lastLifecycleResumeAt = now;
    const listen = this._store.state.session.dayPrivateListen;
    this._dayListenLifecycleResume = !this._isSpectator && listen?.available && listen.enabled
      ? { channel: this._channel, slot: listen.selectedSlot, expiresAt: now + 10000 }
      : null;
    this._store.commit("session/setReconnecting", true);
    this.connect(this._channel);
  }

  _restoreLifecycleDayListen() {
    const intent = this._dayListenLifecycleResume;
    if (!intent) return;
    this._dayListenLifecycleResume = null;
    const session = this._store.state.session;
    if (Date.now() > intent.expiresAt || intent.channel !== this._channel ||
      session.sessionId !== intent.channel || this._leavingRoom || session.roomClosedNotice ||
      !["day-private", "day-public", "nomination"].includes(session.voiceState?.phase) ||
      this._isSpectator) {
      return;
    }
    // _onOpen先发送identity，同一Socket有序处理后再由服务端校验开启请求。
    // 开关、阶段、落座、私聊暂停和目标实例均由服务端裁决；拒绝后不重试，
    // 不等以后权限再次放开而偷偷开启，更不恢复旧媒体令牌。
    if (Number.isInteger(intent.slot) && intent.slot >= 1)
      this.requestVoiceAction({ action: "dayListenStart", slot: intent.slot });
  }

  /**
   * 服务器拒绝了一笔身份发送。目前只有一种原因：旧基线（stale）。
   * 处理原则是「服务器权威优先」：绝不显示假绿点，采纳服务器当前已确认的
   * Delivered，再由真实 Draft 与它重新比较出 无点 / 红 / 绿，并明确告知用户
   * 这次发送没有生效。
   */
  _handleIdentityRejected({ operationId, reason, current } = {}) {
    if (this._isSpectator || this._store.state.session.isSpectator) return;
    if (!operationId) return;
    this._forgetPendingIdentity(operationId);
    if (reason !== "stale") return;
    const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
    const index = Number(current && current.index);
    if (
      Number.isInteger(index) &&
      index >= 0 &&
      (current.gameId || "") === activeGameId
    ) {
      this._store.commit("session/setStorytellerDeliveredIdentity", {
        index,
        roleId: current.roleId || "",
        alignment: current.alignment,
        identityVersion: current.identityVersion
      });
    }
    if (this._isSpectator) return;
    dialog.alert(
      "这一座的身份没有保存：服务器上已经有更新的版本（通常是另一个标签页先发送过）。已改为显示服务器当前确认的身份，请确认后再重新发送。",
      "身份未保存"
    );
  }

  _updateRoleType({ gameId, team, alignment, index, playerId, seatRevision } = {}) {
    if (!this._isSpectator) return;
    const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
    if (activeGameId && gameId !== activeGameId) return;
    if (Number.isInteger(seatRevision) && seatRevision < this._privateSeatRevision) return;
    const ownId = this._store.state.session.playerId;
    if (playerId && playerId !== ownId) return;
    const ownSeat = this._store.state.players.players.findIndex(player =>
      player.id === ownId || (player.coPlayMembers || []).some(member => member.id === ownId));
    if (ownSeat < 0 || (Number.isInteger(index) && index !== ownSeat)) return;
    this._store.commit("session/setPrivateRoleType", {
      playerId: this._store.state.session.playerId,
      team
    });
    this._store.commit("session/setPrivateRoleAlignment", {
      playerId: this._store.state.session.playerId,
      alignment: alignment || team
    });
  }

  // The server requests this only when its virtual private cache is absent
  // while a human storyteller is connected. Send exactly this seat's current
  // storyteller-side identity; other players receive no traffic or change.
  _recoverSeatIdentity({ index, playerId } = {}) {
    if (this._isSpectator || !Number.isInteger(index) || !playerId) return;
    const player = this._store.state.players.players[index];
    if (
      !player ||
      (player.id !== playerId &&
        !(player.coPlayMembers || []).some(member => member && member.id === playerId))
    )
      return;
    // 恢复请求绝不是发牌操作。只请服务端重放已确认的Delivered，
    // 不能拿说书人当前Draft调用发送身份而把未下发的角色自动发出去。
    this._send("identityRecovery", { index, playerId });
  }

  /**
   * Hydrate a replacement storyteller from the virtual storyteller's latest
   * private grimoire. Players and observers never process this packet, and it
   * does not send or announce anything to the table.
   */
  _restoreVirtualGrimoire({ gameId, seats } = {}) {
    if (this._isSpectator || !Array.isArray(seats)) return;
    const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
    if (activeGameId && gameId !== activeGameId) return;
    const delivered = {};
    this._recordAuthority(seats, true);
    // 这份档案是服务器权威的完整私密快照（草稿 + 已送达的并集）。
    const archiveIndexes = new Set();
    seats.forEach(identity => {
      const index = Number(identity && identity.index);
      if (!Number.isInteger(index) || !identity) return;
      archiveIndexes.add(index);
      if (identity.deliveredHasIdentity) {
        delivered[index] = {
          roleId: identity.deliveredRoleId || "",
          alignment: identity.deliveredAlignment === "evil" ? "evil" : "good",
          identityVersion: Math.max(0, Number(identity.deliveredIdentityVersion) || 0)
        };
      }
      if (!identity.hasIdentity) return;
      const player = this._store.state.players.players[index];
      if (!player) return;
      const roleId = String(identity.roleId || "");
      if (!roleId) {
        // 档案里“没有身份”的空条目只表示这个座位还没发牌，绝不能顺手
        // 把它变成蓝色阵营圈（这正是全新房间出现蓝圈/错误阵营的来源）。
        // 这里必须用 setIdentity 而不是 players/update：后者在订阅里会触发
        // 「把座位状态回传给服务器」，而服务器又会广播 gs，形成
        // gs → 回传 → gs 的自我放大循环（换座后刷新私有档案时实测把整条
        // 连接刷到被防刷断开）。setIdentity 只改本地状态，不回传。
        this._store.commit("players/setIdentity", {
          player,
          role: {},
          alignment: ""
        });
        return;
      }
      const role =
        this._store.state.roles.get(roleId) ||
        this._store.getters.rolesJSONbyId.get(roleId) ||
        {};
      this._store.commit("players/setIdentity", {
        player,
        role,
        alignment: identity.alignment
      });
    });
    /*
     * 换座之后，服务器会把迁移结果当成新的权威档案发过来（旧座位已经没有
     * 任何私密状态）。这时继续把草稿角色留在已经空掉的椅子上，说书人看到
     * 的就是「人换走了、角色还留在一张空椅子上、同步点也没了」。
     * 只清理**当前无人**且不在档案里的座位，绝不碰还坐着人的位置。
     */
    this._store.state.players.players.forEach((player, index) => {
      if (archiveIndexes.has(index) || !player || player.id) return;
      const hasDraftRole = !!(player.role && player.role.id);
      if (!hasDraftRole && !delivered[index]) return;
      this._store.commit("players/setIdentity", { player, role: {}, alignment: "" });
      delete delivered[index];
    });
    this._store.commit("session/replaceStorytellerDeliveredIdentities", delivered);
    // 服务器已经把权威档案交回来了，这时才处理重连前没收到确认的发送：
    // 服务器已保存过的直接结清，没保存过的用同一 operationId 安全重试。
    this._flushPendingIdentityOperations();
  }

  // Seed an empty virtual storyteller archive from the connected
  // storyteller's current local grimoire. These are private cache packets,
  // not identity deliveries: no player token, notice, or public state moves.
  _snapshotVirtualGrimoire() {
    if (this._isSpectator) return;
    // 只有真正的说书人连接才会收到 virtualGrimoire(SnapshotNeeded)：与
    // reminderNotes 一样属于服务端权威信号，用它建立/刷新恢复意图。
    this._saveStorytellerIntent();
    this._store.state.players.players.forEach(player =>
      this.cacheSeatIdentity(player)
    );
  }

  /**
   * Owners who are seated as ordinary players may change the seat count, but
   * must not receive storyteller controls. Publish only the public layout.
   */
  sendOwnerSeatLayout() {
    if (!this._store.state.session.isOwner) return;
    this.syncSeatCount(true);
  }

  /** Publish only a requested table length. Seats themselves remain server-owned. */
  syncSeatCount(force = false) {
    const mayConfigure = !this._isSpectator || this._store.state.session.isOwner;
    if (!mayConfigure) return;
    const seatCount = this._store.state.players.players.length;
    /*
     * 首次建房 / 首次进入的初始化阶段，本地桌面可能还没有被权威快照填充
     * （players 长度为 0）。这个 0 只代表“本页还没收到权威座位表”，**不是**
     * 用户的缩桌意图；把它当权威座位数发给服务端会把刚建好的桌子清成 0 座，
     * 之后任何人 `claim` 都会被服务端以 `seatClaim invalid「该座位不存在」`
     * 拒绝——真实玩家页面与按同一协议连接的客户端都会落座失败（生产实测
     * 房间权威座位从 revision 1 的 6 座变成 revision 2 的 0 座）。
     * 座位数只由真实用户动作 `resizeSeatCount()`（增加/减少座位）改写；
     * 这里跳过 0，其余数值保持原样发布。
     */
    if (seatCount === 0) return;
    if (!force && this._authoritativeSeatCount === seatCount) return;
    if (this._send("seatCount", { seatCount, revision: this._seatRevision })) {
      // Do not mark it authoritative until the server echoes seatLayout.
      // A rejected/reconnecting request must remain retryable.
    }
  }

  /** Request a table length and resolve only after server seatLayout confirms it. */
  resizeSeatCount(nextCount) {
    const seatCount = Math.max(0, Math.min(20, Math.floor(Number(nextCount))));
    if (!Number.isFinite(seatCount)) return Promise.reject(new Error("座位数量无效"));
    if (!this._store.state.session.sessionId) {
      const current = this._store.state.players.players.length;
      if (seatCount > current) this._store.commit("players/addSeats", seatCount - current);
      for (let index = current - 1; index >= seatCount; index--) {
        this._store.commit("players/remove", index);
      }
      return Promise.resolve(seatCount);
    }
    if (this._seatResizeRequest) {
      return Promise.reject(new Error("正在等待上一项座位调整完成"));
    }
    return new Promise((resolve, reject) => {
      this._seatResizeRequest = { seatCount, resolve, reject };
      if (!this._send("seatCount", { seatCount, revision: this._seatRevision })) {
        this._settleSeatResize(new Error("房间连接未就绪，请稍后重试"));
        return;
      }
      this._seatResizeTimer = setTimeout(() => {
        this._settleSeatResize(new Error("服务器未确认座位调整，请检查连接后重试"));
      }, 8000);
    });
  }

  _settleSeatResize(error, count) {
    const request = this._seatResizeRequest;
    if (!request) return;
    clearTimeout(this._seatResizeTimer);
    this._seatResizeTimer = null;
    this._seatResizeRequest = null;
    if (error) request.reject(error);
    else request.resolve(count);
  }

  claimStoryteller() {
    // Match the visible room state instead of a reconnect-stale socket flag.
    // Otherwise the button can still read “坐下” while this method returns
    // before sending any storyteller claim request.
    if (!this._store.state.session.isSpectator) return;
    this._isSpectator = true;
    this._send("storyteller");
  }

  /**
   * 说书人恢复意图（sessionStorage，与 clientId 同生命周期）：
   * 只有真的坐上某个房间的说书人席位时才写入；主动起立、离开房间、房间失效、
   * 换房间、被 4001 接管、自动恢复被拒绝时都会删除。刷新/重连后的自动恢复
   * 只认这份意图，所以普通旁观者绝不会被误判成“说书人回来了”。
   */
  _saveStorytellerIntent() {
    if (!this._channel) return;
    try {
      sessionStorage.setItem(
        storytellerIntentKey,
        JSON.stringify({
          room: this._channel,
          clientId: getConnectionId(),
          savedAt: Date.now()
        })
      );
    } catch (error) {
      // 隐私模式等场景写不了 sessionStorage：只是没有恢复意图，不影响主流程。
    }
  }

  _clearStorytellerIntent() {
    try {
      sessionStorage.removeItem(storytellerIntentKey);
    } catch (error) {
      /* 同上：没有可清理的存储 */
    }
  }

  /** 本页在这个房间上是否留有说书人恢复意图（房号 + clientId 双重匹配）。 */
  _hasStorytellerIntent() {
    if (!this._channel) return false;
    try {
      const raw = sessionStorage.getItem(storytellerIntentKey);
      if (!raw) return false;
      const intent = JSON.parse(raw);
      return (
        !!intent &&
        intent.room === this._channel &&
        intent.clientId === getConnectionId()
      );
    } catch (error) {
      return false;
    }
  }

  releaseStoryteller() {
    if (this._isSpectator) return;
    this._dayListenLifecycleResume = null;
    // 主动起立：立刻删掉恢复意图，刷新/重连后也绝不会自动坐回。
    this._clearStorytellerIntent();
    this._send("storyteller", {
      action: "release",
      playerId: this._store.state.session.playerId
    });
  }

  _handleStorytellerSeat(status) {
    const wasRestoring = this._restoringStoryteller;
    if (status === "claimed") {
      this._restoringStoryteller = false;
      clearTimeout(this._storytellerRestoreTimer);
      this._storytellerRestoreTimer = null;
      // 只有真的坐上席位才写恢复意图（手动点「坐下」与自动恢复都走这里）。
      this._saveStorytellerIntent();
      this._spectatorFallbackDone = false;
      // The persisted UI state is authoritative here.  A reconnect can leave
      // the private socket flag one step behind it; using that stale flag used
      // to accept the server claim but skip the client-side storyteller setup.
      if (!this._store.state.session.isSpectator) return;
      this._isSpectator = false;
      this._store.commit("session/setSpectator", false);
      this._store.commit("session/setStorytellerProfile", {
        id: this._store.state.session.playerId,
        ...getLocalProfile()
      });
      // The connection changes into the storyteller only after it has joined.
      // Re-send the profile now so all current and future clients can render
      // the dedicated storyteller seat.
      this._send("identity", {
        id: this._store.state.session.playerId,
        profile: getLocalProfile()
      });
      // The server has already atomically released this browser's former
      // player seat and broadcast the canonical layout before this reply.
      // Do not echo an old local table through the legacy game-state channel.
      // 刷新/重连后的自动恢复不弹窗：避免每次刷新都打扰一次。
      if (!wasRestoring) {
        dialog.alert("你已落座说书人席位，现拥有完整主持权限。", "已成为说书人");
      }
      return;
    }
    if (status === "released") {
      this._restoringStoryteller = false;
      clearTimeout(this._storytellerRestoreTimer);
      this._storytellerRestoreTimer = null;
      this._clearStorytellerIntent();
      // 「发送整本魔典」与说书人席位共用 canViewGrimoire 这一显示授权。
      // 离开说书人席位后必须先撤销旧授权；即使重复收到 released、客户端
      // 已经处于 spectator 状态，也不能让上一身份留下的完整魔典继续可见。
      // 复盘公开由 isPostGameReview 单独裁决，不受这里影响。
      this._store.commit("session/setCanViewGrimoire", false);
      this._isSpectator = true;
      this._revokeStorytellerPrivateView();
      if (this._store.state.session.isSpectator) return;
      this._isSpectator = true;
      this._store.commit("session/setSpectator", true);
      this._store.commit("session/setStorytellerProfile", {
        id: "",
        name: "空",
        avatar: ""
      });
      dialog.alert("你已起立并释放说书人席位。", "已起立");
      return;
    }
    // 其余情况 = 服务端回复 occupied：席位已被另一个合法客户端占用。
    this._restoringStoryteller = false;
    clearTimeout(this._storytellerRestoreTimer);
    this._storytellerRestoreTimer = null;
    if (wasRestoring) {
      // 自动恢复失败：放弃恢复意图，保持旁观；不弹窗、不反复重试。
      this._clearStorytellerIntent();
      return;
    }
    if (!this._isSpectator) return;
    dialog.alert("说书人席位当前有人主持，暂时无法认领。", "席位已占用");
  }

  _revokeStorytellerPrivateView() {
    this._resetBluffDraft();
    this._store.commit("session/revokeStorytellerPrivateView");
    this._store.commit("players/clearStorytellerPrivateView", {
      keepPublishedReview: this._store.state.session.isPostGameReview
    });
    this._remoteReminderNotes = null;
    this._pendingReminderNotes = null;
    this._deliveredRoles = {};
    this._pendingIdentityOperations.clear();
    this._pendingAlignmentOperations.clear();
  }

  /**
   * 房间已经不存在（被解散/超时销毁/代际失效）。
   * 这里做两件事：立刻退出房间并清掉旧桌面（不再显示旧 seats/roles/标记），
   * 以及留下一个明确的“房间已失效”状态给界面显示提示与“返回主页”。
   * 不会用同一个 roomId 自动重建房间。
   */
  _handleRoomClosed(message, reason = "manual") {
    // 房间失效（解散 / 空房超时 / 挂机超时 / 代际失效）：恢复意图一并作废，
    // 旧标签页刷新或重连都不会再 claim，也不会复活这个房间。
    this._clearStorytellerIntent();
    this._restoringStoryteller = false;
    clearTimeout(this._storytellerRestoreTimer);
    this._storytellerRestoreTimer = null;
    this._reconnectAttempt = 0;
    this._reconnectStartedAt = 0;
    this._store.commit("session/leaveRoom");
    this._store.commit("session/setRoomClosedNotice", {
      reason,
      message: message || roomClosedMessage(reason)
    });
  }

  kickPlayer(playerId) {
    if (!this._store.state.session.isOwner || !playerId) return;
    this._send("kick", playerId);
  }

  disbandRoom() {
    if (!this._store.state.session.isOwner) return;
    this._send("disband");
  }

  /**
   * Update a player id associated with that seat.
   * @param index seat index or -1
   * @param value playerId to add / remove
   * @private
   */
  _updateSeat([index, value], isAuthoritative = false) {
    if (
      !isAuthoritative &&
      this._isSpectator &&
      !this._store.state.session.isOwner
    )
      return;
    const profile =
      typeof value === "object" && value
        ? value
        : { id: value, name: "空", avatar: "" };
    const playerId = profile.id || "";
    const property = "id";
    const players = this._store.state.players.players;
    // Clear every old occurrence, not only the first one. Server-authoritative
    // seat releases use index -1 specifically to heal a stale duplicate left
    // behind by an older browser snapshot before the current seat is applied.
    const applySeat = () => {
    const oldIndexes = players
      .map((player, playerIndex) => (player.id === playerId ? playerIndex : -1))
      .filter(playerIndex => playerIndex >= 0 && playerIndex !== index);
    oldIndexes.forEach(oldIndex => {
      this._store.commit("players/update", {
        player: players[oldIndex],
        property,
        value: ""
      });
      this._store.commit("players/update", {
        player: players[oldIndex],
        property: "name",
        value: "空"
      });
      this._store.commit("players/update", {
        player: players[oldIndex],
        property: "avatar",
        value: ""
      });
      // 旧座位残留的共玩成员一并清掉，否则复制出来的座位会带着共玩标识。
      this._store.commit("players/update", {
        player: players[oldIndex],
        property: "coPlayMembers",
        value: []
      });
    });
    // add playerId to new seat
    if (index >= 0) {
      const player = players[index];
      if (!player) return;
      this._store.commit("players/update", {
        player,
        property,
        value: playerId
      });
      // 共玩成员是公开状态，服务器在座位包里带着它；此前客户端只应用了
      // id／name／avatar，导致“起立后没有共玩了，共玩标识还留着”。
      if (Object.prototype.hasOwnProperty.call(profile, "coPlayMembers")) {
        this._store.commit("players/update", {
          player,
          property: "coPlayMembers",
          value: Array.isArray(profile.coPlayMembers) ? profile.coPlayMembers : []
        });
      }
      if (playerId) {
        this._store.commit("players/update", {
          player,
          property: "name",
          value: profile.name || "空"
        });
        this._store.commit("players/update", {
          player,
          property: "avatar",
          value: profile.avatar || ""
        });
      } else {
        // A server seat packet with an empty ID is an explicit vacancy, not
        // merely an omitted profile field. Leaving its old name/avatar in
        // place caused the just-vacated chair to retain the previous player
        // visually until a later move happened to overwrite it.
        this._store.commit("players/update", {
          player,
          property: "name",
          value: "空"
        });
        this._store.commit("players/update", {
          player,
          property: "avatar",
          value: ""
        });
      }
      this._store.commit("players/update", {
        player,
        property: "coPlayMembers",
        value: Array.isArray(profile.coPlayMembers) ? profile.coPlayMembers : []
      });
    }
    // Only the storyteller maintains the connected-player counter. Passing a
    // boolean here previously corrupted that list and could make a successful
    // seat appear to vanish on another browser.
    if (!this._isSpectator && playerId) this._handlePing([playerId, 0]);
    };
    if (!isAuthoritative) {
      applySeat();
      return;
    }
    this._isApplyingRemoteSeatLayout = true;
    try {
      applySeat();
    } finally {
      this._isApplyingRemoteSeatLayout = false;
    }
  }

  /**
   * Apply the server's complete public seat layout without touching private
   * role/alignment data. Vacant rows deliberately clear old local avatars and
   * IDs so a browser cannot retain a phantom seat after reconnecting.
   */
  _applyAuthoritativeSeatLayout({ seats, seatCount, revision } = {}) {
    if (!Array.isArray(seats)) return;
    this._isApplyingRemoteSeatLayout = true;
    try {
      const players = this._store.state.players.players;
      const count = Math.max(0, Number.isInteger(seatCount) ? seatCount : seats.length);
      this._authoritativeSeatCount = count;
      if (players.length < count) {
        this._store.commit("players/addSeats", count - players.length);
      } else if (players.length > count) {
        // `seatLayout` is received by storytellers as well as spectators.
        // Unlike a public `gs`, a storyteller does not apply that gs packet,
        // so the authoritative layout itself must remove surplus rows.
        // Without this branch players saw an immediate empty table after
        // “移除全部”, while the storyteller retained the old row count.
        for (let index = players.length - 1; index >= count; index--) {
          this._store.commit("players/remove", index);
        }
      }
      const affected = Math.max(players.length, count);
      for (let index = 0; index < affected; index++) {
        const player = players[index];
        if (!player) continue;
        const profile = seats[index] || { id: "", name: "空", avatar: "", coPlayMembers: [] };
        this._store.commit("players/update", { player, property: "id", value: profile.id || "" });
        this._store.commit("players/update", {
          player,
          property: "name",
          value: profile.id ? (profile.name || "空") : "空"
        });
        this._store.commit("players/update", {
          player,
          property: "avatar",
          value: profile.id ? (profile.avatar || "") : ""
        });
        this._store.commit("players/update", {
          player,
          property: "coPlayMembers",
          value: Array.isArray(profile.coPlayMembers) ? profile.coPlayMembers : []
        });
      }
      if (Number.isInteger(revision)) {
        this._seatRevision = Math.max(this._seatRevision, revision);
      }
      if (
        this._seatResizeRequest &&
        this._seatResizeRequest.seatCount === count
      ) {
        this._settleSeatResize(null, count);
      }
      const ownSeat = players.findIndex(player =>
        player.id === this._store.state.session.playerId ||
        (Array.isArray(player.coPlayMembers) && player.coPlayMembers.some(member =>
          member && member.id === this._store.state.session.playerId
        ))
      );
      const nextOwnSeat = ownSeat >= 0 ? ownSeat : null;
      const ownSeatChanged=nextOwnSeat !== this._reportedSeatIndex;
      if (ownSeatChanged) {
        // 只按本人落座/换座代次拒绝旧私密响应。别人换座不应丢弃本人的合法下发。
        this._privateSeatRevision = Math.max(this._privateSeatRevision, Number(revision) || 0);
        if (this._isSpectator) {
          if (!this._store.state.session.canViewGrimoire && !this._store.state.session.isPostGameReview)
            this._store.commit("players/clearIdentityState");
          this._store.commit("session/clearOwnPrivateIdentity", this._store.state.session.playerId);
          this._receivedBluffVersion=0;
          this._store.commit('players/setBluff');
        }
      }
      this._reportedSeatIndex = nextOwnSeat;
      if(this._isSpectator && ownSeat>=0 && ownSeatChanged) {
        this._send('seatPrivateSync');
      }
      this._store.commit("session/setConfirmedSeat", ownSeat);
      if (ownSeat >= 0) {
        // 从说书人/授权魔典视角改坐玩家位时，权限必须降为当前座位玩家：
        // 只保留自己的私密身份；其它座位显示头像或底牌。结束游戏后的公开
        // 复盘仍由 isPostGameReview 优先展示权威身份。
        this._store.commit("session/setCanViewGrimoire", false);
        this._syncVoiceRoom(true);
      }
    } finally {
      this._isApplyingRemoteSeatLayout = false;
    }
  }

  /**
   * Privately deliver only role assignments that changed since the last send.
   * The server routes each sub-message to its named player only, so unrelated
   * players receive neither a notification nor a state update that could hint
   * at an in-game identity change.
   */
  distributeRoles(force = false) {
    if (this._isSpectator) return;
    const message = {};
    this._store.state.players.players.forEach((player, index) => {
      // Every seat keeps its assigned role in the storyteller's grimoire,
      // including an empty/offline seat. Only a connected seat has someone
      // to receive a private online message.
      this.cacheSeatIdentity(player);
      // 「发送整本魔典」同样是一次明确的发送动作：每个座位都要留下
      // delivered 记录（含当前无人的座位），否则中途入座的人拿不到身份。
      this._publishSeatIdentity(
        index,
        (player.role && player.role.id) || "",
        storytellerTokenAlignment(player)
      );
      const recipients = this.seatRecipientIds(player);
      if (!recipients.length) return;

      const roleId = (player.role && player.role.id) || "";
      const alignment = storytellerTokenAlignment(player);
      const identityKey = `${roleId}|${alignment}`;
      if (!force && recipients.every(id => this._deliveredRoles[id] === identityKey)) return;

      // Send an empty role as well when a previously assigned role was
      // removed, so only that player's private token is cleared.
      if (roleId || recipients.some(id => this._deliveredRoles[id])) {
        recipients.forEach(recipientId => {
          message[recipientId] = ["identity", {
            gameId: (this._store.state.session.voiceState.game || {}).id || "",
            index,
            playerId: recipientId,
            roleId,
            alignment,
            notify: false
          }];
        });
      }
    });
    if (Object.keys(message).length) this._send("direct", message);
  }

  /** Deliver the configured identity to one seat, with a private notice. */
  requestPrivateAction(type, payload = {}) {
    if (this._isSpectator || !["identityDisclosure", "offlineAssignment"].includes(type)) return Promise.reject(Error("仅说书人可以执行此操作"));
    const requestId = this._newIdentityOperationId();
    const gameId = this._store.state.session.voiceState.game?.id || "";
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => finish({ ok: false, message: "请求未确认，请恢复当前状态后重试" }), 8000);
      const finish = response => {
        clearTimeout(timer); this._privateActionPending.delete(requestId);
        if (response.ok && !this._isSpectator && this._store.state.session.voiceState.game?.id === gameId) resolve(response);
        else reject(Error(response.message || "当前游戏或权限已变化"));
      };
      this._privateActionPending.set(requestId, { finish });
      if (!this._send(type, { ...payload, gameId, requestId })) finish({ ok: false, message: "连接不可用，请重连后恢复状态" });
    });
  }

  syncCurrentDraft() {
    if (this._isSpectator) return;
    this._store.state.players.players.forEach(p => this.cacheSeatIdentity(p));
  }

  sendPlayerIdentity(player, notify = true) {
    if (this._isSpectator || !player) return;
    const index = this._store.state.players.players.indexOf(player);
    if (index < 0) return;
    const roleId = (player.role && player.role.id) || "";
    const alignment = storytellerTokenAlignment(player);
    // Keep the virtual storyteller current even if this is an offline/empty
    // seat. This sends no card to anyone; it only enables a later valid seat
    // claim to recover the configuration privately.
    this._send("identitySnapshot", {
      gameId: (this._store.state.session.voiceState.game || {}).id || "",
      index,
      roleId,
      alignment
    });
    // 「发送完整角色」这个动作本身就是权威的已送达记录：即使该座位当前
    // 无人（收件人集合为空、下面一个 direct 都不会发出），服务端也要把
    // 这份身份记成 delivered，后来落座的人才能直接拿到它。
    // 只发草稿（没按发送）时绝不会走这里，所以草稿仍然不会泄漏给玩家。
    this._publishSeatIdentity(index, roleId, alignment);
    this.seatRecipientIds(player).forEach(playerId => {
      this._sendDirect(playerId, "identity", {
        index,
        playerId,
        gameId: (this._store.state.session.voiceState.game || {}).id || "",
        roleId,
        alignment,
        notify
      });
    });
  }

  /**
   * 身份发送事务：把「发送这一座的身份」当成一笔有编号的操作。
   *
   * 编号的用途是幂等。WebSocket 短暂断开、ACK 丢在路上、重连后的安全重试
   * 都会让同一笔发送再次到达服务器；服务器按 operationId 识别出「这是同一
   * 笔」，原样返回第一次的结果，不再推进版本、不重复产生副作用。
   *
   * baseVersion 是「发起这次发送时，我看到的这一座 Delivered 版本」。服务端
   * 只在这个版本仍然是最新时才接受；另一个标签页已经先写过时，旧基线的写入
   * 会被拒为 stale，旧页面不可能把已确认的新身份覆盖回去。
   *
   * 版本 scope 刻意只取单座 Delivered 版本，不是整本云端魔典的全局版本：
   * 草稿、状态 Token、其它座位的变化都不应该让一次身份发送失效，将来多个
   * 说书人共同编辑同一本云端魔典时也不需要推翻这里的语义。
   */
  _publishSeatIdentity(index, roleId, alignment) {
    const gameId = (this._store.state.session.voiceState.game || {}).id || "";
    const delivered = this._store.state.session.storytellerDeliveredIdentities[index];
    const authority = this._store.state.session.storytellerAuthoritativeIdentities[index];
    const baseVersion = Math.max(0, Number(delivered && delivered.identityVersion) || 0, Number(authority && authority.authorityVersion) || 0);
    const operationId = this._newIdentityOperationId();
    this._pendingIdentityOperations.set(operationId, {
      index,
      gameId,
      roleId: roleId || "",
      alignment: alignment === "evil" ? "evil" : "good",
      baseVersion
    });
    this._store.commit("session/setStorytellerPendingIdentity", {
      index,
      operationId,
      roleId: roleId || "",
      alignment
    });
    this._sendIdentityOperation(operationId);
    return operationId;
  }

  /** 发送（或重发）一笔身份操作。发送失败不丢账，留给重连后重试。 */
  _sendIdentityOperation(operationId) {
    const operation = this._pendingIdentityOperations.get(operationId);
    if (!operation) return false;
    return this._send("identityPublish", {
      gameId: operation.gameId,
      index: operation.index,
      roleId: operation.roleId,
      alignment: operation.alignment,
      operationId,
      baseVersion: operation.baseVersion
    });
  }

  _newIdentityOperationId() {
    return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  /** 结清一笔发送（收到 ACK、被拒、跨局作废）。只清同一 operationId。 */
  _forgetPendingIdentity(operationId) {
    const operation = this._pendingIdentityOperations.get(operationId);
    if (!operation) return;
    this._pendingIdentityOperations.delete(operationId);
    this._store.commit("session/clearStorytellerPendingIdentity", {
      index: operation.index,
      operationId
    });
  }

  /**
   * 重连 / 重新成为说书人之后处理尚未确认的发送：
   *   · 服务器其实已经保存过这笔（只是 ACK 丢在路上）→ 直接结清，不重发；
   *   · 服务器没收到 → 用同一个 operationId 安全重试（服务端幂等）；
   *   · 已经不属于当前局 → 作废，不让上一局的发送污染新局。
   */
  _flushPendingIdentityOperations() {
    this._flushPendingAlignmentOperations();
    if (!this._pendingIdentityOperations.size) return;
    const activeGameId = (this._store.state.session.voiceState.game || {}).id || "";
    [...this._pendingIdentityOperations.keys()].forEach(operationId => {
      const operation = this._pendingIdentityOperations.get(operationId);
      if (!operation) return;
      if ((operation.gameId || "") !== activeGameId) {
        this._forgetPendingIdentity(operationId);
        return;
      }
      const delivered = this._store.state.session.storytellerDeliveredIdentities[
        operation.index
      ];
      const authority = this._store.state.session.storytellerAuthoritativeIdentities[operation.index];
      if (this._store.state.session.voiceState.game?.allRecall && authority?.authorityOperationId === operationId) {
        this._forgetPendingIdentity(operationId);
        return;
      }
      if (
        delivered &&
        delivered.roleId === operation.roleId &&
        delivered.alignment === operation.alignment
      ) {
        // 服务器权威状态已经等于这笔发送的内容：确认过，只是回执没到。
        this._forgetPendingIdentity(operationId);
        return;
      }
      this._sendIdentityOperation(operationId);
    });
  }

  seatRecipientIds(player) {
    if (!player) return [];
    const coPlayers = Array.isArray(player.coPlayMembers) ? player.coPlayMembers : [];
    const ids = coPlayers.length === 2
      ? coPlayers.map(member => member && member.id)
      : [player.id];
    return [...new Set(ids.filter(Boolean))];
  }

  distributeRoleTypes() {
    if (this._isSpectator) return;
    if (this._pendingAlignmentOperations.size) return;
    const session=this._store.state.session;
    const seats=this._store.state.players.players.map((p,index)=>({
      index,roleId:p.role?.id || "",alignment:storytellerTokenAlignment(p),
      baseVersion:Math.max(Number(session.storytellerDeliveredIdentities[index]?.identityVersion)||0,Number(session.storytellerAuthoritativeIdentities[index]?.authorityVersion)||0)
    }));
    const operationId="align-"+this._newIdentityOperationId();
    const payload={gameId:session.voiceState.game?.id || "",seatRevision:this._seatRevision,seats,operationId};
    this._pendingAlignmentOperations.set(operationId,payload);
    this._send("identityAlignmentPublish",payload);
  }

  _recordAuthority(seats, replace=false) {
    const next=replace?{}:{...this._store.state.session.storytellerAuthoritativeIdentities};
    (seats||[]).forEach(s=>{
      if(!s || (!s.authorityHasIdentity && !s.deliveredHasAlignment) || !Number.isInteger(s.index))return;
      if((Number(next[s.index]?.authorityVersion)||0)>(Number(s.authorityVersion)||0))return;
      if((Number(next[s.index]?.authorityVersion)||0)===(Number(s.authorityVersion)||0) && (Number(next[s.index]?.deliveredAlignmentVersion)||0)>(Number(s.deliveredAlignmentVersion)||0))return;
      next[s.index]={...s};
    });
    this._store.commit("session/setStorytellerAuthority",next);
  }

  _flushPendingAlignmentOperations() {
    const session=this._store.state.session;
    for(const [id,operation] of this._pendingAlignmentOperations) {
      if(this._isSpectator || operation.gameId !== session.voiceState.game?.id) {this._pendingAlignmentOperations.delete(id);continue;}
      if(operation.seats.every(s=>session.storytellerAuthoritativeIdentities[s.index]?.authorityOperationId===id))this._pendingAlignmentOperations.delete(id);
      else this._send("identityAlignmentPublish",operation);
    }
  }

  cacheSeatIdentity(player) {
    if (this._isSpectator || !player) return;
    const index = this._store.state.players.players.indexOf(player);
    if (index < 0) return;
    this._send("identitySnapshot", {
      gameId: (this._store.state.session.voiceState.game || {}).id || "",
      index,
      roleId: (player.role && player.role.id) || "",
      alignment: storytellerTokenAlignment(player)
    });
  }

  /**
   * Publish the storyteller's private token reminders (提示标记) to the
   * server so they survive a dead phone, a refresh, or a browser change.
   * This is a private storyteller archive: the server stores it in the
   * 0600 state file and only ever sends it back to a storyteller seat.
   * Players and observers never receive it, and it is deliberately kept
   * out of the public gamestate.
   */
  publishReminders() {
    if (this._isSpectator) return;
    // Remote updates are applied through the same players/update mutation,
    // so ignore the echo they cause instead of bouncing the packet back.
    if (this._applyingReminderNotes) return;
    const snapshot = this._store.state.players.players
      .map((player, index) => [
        index,
        (player.reminders || []).map(reminder => ({
          role: reminder.role,
          name: reminder.name,
          image: reminder.image,
          imageAlt: reminder.imageAlt
          ,...(reminder.id ? { id: reminder.id } : {})
        }))
      ])
      .filter(entry => entry[1].length);
    this._pendingReminderNotes = new Map(
      snapshot.map(([index, reminders]) => [index, reminders])
    );
    this._send("reminderNotes", snapshot);
  }

  _applyReminderNotes(seats) {
    const snapshot = Array.isArray(seats) ? seats : [];
    this._remoteReminderNotes = new Map(
      snapshot
        .filter(entry => Array.isArray(entry) && Number.isInteger(Number(entry[0])))
        .map(entry => [Number(entry[0]), Array.isArray(entry[1]) ? entry[1] : []])
    );
    this._applyingReminderNotes = true;
    try {
      this._remoteReminderNotes.forEach((reminders, index) => {
        const player = this._store.state.players.players[index];
        if (!player) return;
        this._store.commit("players/update", {
          player,
          property: "reminders",
          value: reminders
        });
      });
    } finally {
      this._applyingReminderNotes = false;
    }
  }

  /**
   * A storyteller's archive can arrive before the seat layout is rendered.
   * Re-apply it whenever seats are added so restored markers still land on
   * the right seats instead of being dropped silently.
   */
  _reapplyReminderNotes() {
    if (this._isSpectator || !this._remoteReminderNotes) return;
    this._applyReminderNotes([...this._remoteReminderNotes.entries()]);
  }

  requestCoPlay(player) {
    if (!this._isSpectator || !player || !player.id || !this._store.state.session.sessionId) return;
    this._send("coPlay", {
      action: "request",
      seatIndex: this._store.state.players.players.indexOf(player),
      profile: getLocalProfile()
    });
  }

  kickSeat(payload = {}) {
    if (this._isSpectator || !Number.isInteger(payload.index)) return;
    this._send("seatKick", payload);
  }

  async _handleCoPlay(message = {}) {
    if (message.type === "cooldown") {
      const minutes = Math.max(1, Math.ceil(Number(message.remainingMs || 0) / 60000));
      dialog.alert(`该座位的共玩申请冷却中，请约 ${minutes} 分钟后再试。`, "共玩申请");
      return;
    }
    if (message.type === "approved") {
      dialog.alert("共玩申请已同意。你已加入该座位，并会收到该座位的私密身份信息。", "已加入共玩");
      return;
    }
    if (message.type === "rejected") {
      dialog.alert("该玩家未同意共玩申请。", "共玩申请");
      return;
    }
    if (message.type !== "request" || !message.applicant || !Number.isInteger(message.seatIndex)) return;
    const applicant = message.applicant;
    const accepted = await dialog.confirm(
      `“${applicant.name || "玩家"}”申请与你共玩当前座位。共玩后两人共享一个角色、阵营、投票与死亡状态，是否同意？`,
      "共玩申请"
    );
    this._send("coPlay", accepted ? {
      action: "approve",
      seatIndex: message.seatIndex,
      applicant
    } : {
      action: "reject",
      seatIndex: message.seatIndex,
      applicantId: applicant.id
    });
  }

  requestVoiceAction(payload = {}) {
    if (!this._store.state.session.sessionId) return;
    if (["dayListenStart", "dayListenStop"].includes(payload.action)) this._dayListenLifecycleResume = null;
    if (payload.action === "token") {
      payload = { ...payload, voiceRequestId: ++this._voiceTokenRequestId };
      if (this._voiceMicEntry && this._voiceMicEntry.roomId === payload.roomId) {
        payload.autoStartMic = true;
        payload.micEntryId = this._voiceMicEntry.micEntryId;
      }
    }
    this._send("voice", payload);
  }

  /**
   * 服务端确认进入新一局（或本局作废）后，清掉所有“上一局专属”的本地状态。
   * 只清局内状态：房间、座位、落座玩家、共玩关系、剧本与玩家档案都不动。
   * 由服务端广播触发，所以手机、电脑、第二个说书人页面都会同步清干净。
   */
  _clearGameScopedState() {
    this._resetBluffDraft();
    this._store.commit('session/setReceivedGrimoire', null);
    this._reviewLoadGeneration = (this._reviewLoadGeneration || 0) + 1;
    this._reviewImageProbes?.forEach(cancel => cancel());
    // 说书人私人提示标记的本地缓存。
    this._remoteReminderNotes = null;
    this._pendingReminderNotes = null;
    // 已投递身份的指纹，否则新一局同名空角色不会再发给玩家。
    this._deliveredRoles = {};
    // 上一局的发送事务不能跨局：pending 立即作废，重连也不会把它们重发到新局。
    this._pendingIdentityOperations.clear();
    this._pendingAlignmentOperations.clear();
    this._store.commit("session/clearStorytellerPendingIdentities");
    this._store.commit("session/clearDeliveredIdentities");
    // Player browsers also keep the delivered role on their local seat object.
    // Clear that face immediately instead of waiting for a refresh/snapshot.
    this._store.commit("players/clearIdentityState");
    this._store.commit("session/nomination", {
      nomination: false,
      votes: [],
      lockedVote: 0,
      isVoteInProgress: false
    });
    if (this._isSpectator) return;
    // 说书人页面是角色/阵营的本地权威，必须同步清空座位身份与局内状态。
    this._store.commit("players/clearRoundState");
    this._store.commit("session/clearMarkedPlayer");
    this._store.commit("session/clearPendingDayResolution");
    // 新一局从第 1 夜开始，说书人页面把魔典切回黑夜；其它浏览器跟随
    // 说书人广播的 isNight 状态。
    if (!this._store.state.grimoire.isNight) {
      this._store.commit("setNight", true);
    }
  }

  _syncVoiceRoom(force = false) {
    const { session } = this._store.state;
    const state = session.voiceState || {};
    const id = this._isSpectator ? session.playerId : "storyteller";
    let roomId = "";
    if (["night", "day-private", "day-public", "nomination"].includes(state.phase)) {
      const privateRoom = (state.rooms || []).find(
        item => (item.memberIds || []).includes(id)
      );
      roomId = privateRoom ? privateRoom.id : "public";
    }
    if (!force && roomId === this._requestedVoiceRoom) return;
    if (this._voiceMicEntry && this._voiceMicEntry.roomId !== roomId) this._voiceMicEntry = null;
    this._requestedVoiceRoom = roomId;
    if (roomId) this.requestVoiceAction({ action: "token", roomId });
    else this._store.commit("session/setVoiceToken", null);
  }

  async _handleVoiceRequest(request = {}) {
    if (request.type === "coplay-invite") {
      if (await dialog.confirm("共玩伙伴邀请你进入仅两人可听的共玩语音。进入后会退出当前其他私聊语音，是否同意？", "共玩语音")) {
        this.requestVoiceAction({ action: "acceptCoPlayVoice", inviteId: request.id });
      }
      return;
    }
    if (request.type === "left-coplay") {
      this._syncVoiceRoom(true);
      return;
    }
    if (request.type === "chair-speaker") {
      this._voiceMicEntry = request.micEntryId ? { roomId: "public", micEntryId: request.micEntryId } : null;
      // 这是服务器在“设为主席麦”后给指定玩家的专属指令；令牌会经过
      // 服务端主席资格校验后才带自动开麦标记。
      this.requestVoiceAction({ action: "token", roomId: "public", autoStartMic: true });
      return;
    }
    if (request.type === "invite") {
      /*
       * 弹窗只显示**座位号**（服务端下发 senderSeat / memberSeats），不显示任何
       * playerId、socketId、LiveKit identity 或房间 UUID；数字不齐时退回原来的
       * 通用文案。
       */
      const inviter = Number(request.senderSeat) > 0 ? `${Number(request.senderSeat)}号玩家` : "说书人";
      const members = Array.isArray(request.memberSeats)
        ? request.memberSeats.filter(seat => Number(seat) > 0).map(Number)
        : [];
      const memberText = members.length ? `当前私聊成员：${members.join("、")}。` : "";
      if (await dialog.confirm(`${inviter}邀请你私聊。${memberText}是否接受？`, "语音邀请")) {
        this.requestVoiceAction({ action: "acceptInvite", inviteId: request.id });
      }
      return;
    }
    if (request.type === "join-request") {
      const applicant =
        Number(request.senderSeat) > 0 ? `${Number(request.senderSeat)}号玩家` : "一名玩家";
      if (
        await dialog.confirm(
          `${applicant}申请加入当前语音房。所有成员都同意后才会加入。`,
          "加入语音房"
        )
      ) {
        this.requestVoiceAction({ action: "voteJoin", requestId: request.id, approve: true });
      } else {
        this.requestVoiceAction({ action: "voteJoin", requestId: request.id, approve: false });
      }
      return;
    }
    if (request.type === "join" && request.roomId) {
      this._requestedVoiceRoom = request.roomId;
      this._voiceMicEntry = request.micEntryId ? { roomId: request.roomId, micEntryId: request.micEntryId } : null;
      this.requestVoiceAction({ action: "token", roomId: request.roomId });
      /*
       * 被说书人强制拉入夜间多人语音时给玩家一个明确提示。
       * 只对玩家端（非说书人视角）弹，说书人自己发起的不再重复提示；
       * 提示不是确认步骤——语音已经在同一时刻由服务端授权切过去了。
       */
      if (request.purpose === "night-group" && this._isSpectator) {
        dialog.alert("你已经被说书人拉入多人语音私聊房间", "多人语音");
      }
      return;
    }
    // 夜间多人语音：筛选后一个玩家都没命中（空集合）。这里如实反馈，
    // 不假装创建成功；按钮状态仍然由服务端权威 voiceState 决定。
    if (request.type === "night-group-empty") {
      dialog.alert(request.text || "当前没有可加入多人语音的玩家", "多人语音");
    }
  }

  setBootlegger(content) {
    if (this._isSpectator) return;
    this._send("bootlegger", content || "");
  }

  setUseOldOrder(settings) {
    if (this._isSpectator) return;
    this._send("useOldOrder", settings);
  }

  setUseOldRole(settings) {
    if (this._isSpectator) return;
    this._send("useOldRole", settings);
  }

  /**
   * Standard first-night bluff delivery: only seated evil Demon characters.
   * A good/blue Demon (for example, created by the Pit-Hag) is deliberately
   * excluded; the storyteller can use the directional action for exceptions.
   */
  distributeBluffs() {
    if (this._isSpectator) return;
    const { players, bluffs } = this._store.state.players;
    const bluffIds = bluffs.filter(role => role && role.id).map(role => role.id);
    if (bluffIds.length !== 3) return;
    const targets = [];
    players.forEach(player => {
      if (
        player.id &&
        player.role &&
        player.role.team === "demon" &&
        storytellerTokenAlignment(player) === "evil"
      ) {
        targets.push(players.indexOf(player));
      }
    });
    this._publishBluffs(targets,bluffIds);
  }

  /**
   * Send the current three bluffs to explicitly chosen seated seats.
   * This deliberately does not infer team/alignment: the storyteller made
   * the target choice in the dedicated directional-send UI.
   */
  distributeBluffsToSeat({ seatNumber, seatNumbers } = {}) {
    if (this._isSpectator) return;
    const { players, bluffs } = this._store.state.players;
    const bluffIds = bluffs.filter(role => role && role.id).map(role => role.id);
    const requestedSeats = Array.isArray(seatNumbers) ? seatNumbers : [seatNumber];
    const normalizedSeats = [...new Set(requestedSeats.map(Number))];
    const targets = normalizedSeats.map(number => players[number - 1]);
    // Directional send is all-or-nothing. A typo must not quietly send to a
    // subset of the intended players after the dialog validated its list.
    if (
      bluffIds.length !== 3 ||
      !normalizedSeats.length ||
      normalizedSeats.some(number => !Number.isInteger(number)) ||
      targets.some(player => !player || !player.id)
    )
      return;
    this._publishBluffs(targets.map(p=>players.indexOf(p)),bluffIds);
  }

  refreshBluffEligibility() { if(!this._isSpectator)this._send('bluffEligibility'); }

  // 私密草稿自动保存仅回到说书人；发送给玩家必须另走bluffPublish。
  _resetBluffDraft() {
    clearTimeout(this._pendingStorytellerBluffs?.timer);
    this._storytellerBluffScope='';this._storytellerBluffRecord=null;
    this._storytellerBluffEdit=null;this._pendingStorytellerBluffs=null;
    this._store.commit('session/setBluffDraftStatus',{state:'',message:''});
  }

  _bluffDraftScope() {return this._channel+':'+(this._store.state.session.voiceState.game?.id||'');}

  saveStorytellerBluffs() {
    const gameId=this._store.state.session.voiceState.game?.id;
    if(this._isSpectator||this._applyingBluffDraft||!gameId||!this._channel)return;
    this._storytellerBluffEdit={scope:this._bluffDraftScope(),roleIds:this._store.state.players.bluffs.map(r=>r?.id||'')};
    this._store.commit('session/setBluffDraftStatus',{state:'saving',message:'正在保存说书人草稿（未发送给玩家）…'});
    if(this._storytellerBluffScope!==this._bluffDraftScope())this._send('storytellerBluffs');
    else this._flushBluffDraft();
  }

  _flushBluffDraft() {
    if(this._isSpectator||this._pendingStorytellerBluffs||this._storytellerBluffEdit?.scope!==this._bluffDraftScope())return;
    const edit=this._storytellerBluffEdit;
    if(JSON.stringify(edit.roleIds)===JSON.stringify(this._storytellerBluffRecord?.roleIds||[])) {
      this._storytellerBluffEdit=null;this._applyBluffDraft();return;
    }
    const request={gameId:this._store.state.session.voiceState.game?.id||'',operationId:'bluff-draft-'+this._newIdentityOperationId(),
      baseVersion:this._storytellerBluffRecord?.version||0,roleIds:[...edit.roleIds]};
    const pending={request};this._pendingStorytellerBluffs=pending;
    pending.timer=setTimeout(()=>{
      if(this._pendingStorytellerBluffs===pending)this._store.commit('session/setBluffDraftStatus',{
        state:'failed',message:'说书人草稿尚未确认保存；选择仍在本页，请恢复连接后重试，勿刷新。'});
    },8000);
    this._send('storytellerBluffs',request);
  }

  _receiveStorytellerBluffs(p) {
    if(this._isSpectator||p?.roomId!==this._channel||p.gameId!==(this._store.state.session.voiceState.game?.id||''))return;
    const scope=this._bluffDraftScope(),record=p.record||null,pending=this._pendingStorytellerBluffs;
    if(this._storytellerBluffScope===scope&&(record?.version||0)<(this._storytellerBluffRecord?.version||0))return;
    this._storytellerBluffScope=scope;this._storytellerBluffRecord=record;
    if(pending) {
      if(record?.operationId===pending.request.operationId||p.operationId===pending.request.operationId) {
        clearTimeout(pending.timer);this._pendingStorytellerBluffs=null;
      } else if((record?.version||0)===pending.request.baseVersion) {
        // 查询/重连补发时，使用同一操作ID重试，不制造第二笔保存。
        this._send('storytellerBluffs',pending.request);return;
      } else {
        clearTimeout(pending.timer);this._pendingStorytellerBluffs=null;
        this._store.commit('session/setBluffDraftStatus',{state:'failed',message:'服务器草稿已变化，当前选择已保留，请核对后重新编辑。'});return;
      }
    }
    if(p.ok===false) {
      this._store.commit('session/setBluffDraftStatus',{state:'failed',message:p.message||'草稿未保存，选择已保留。'});return;
    }
    if(this._storytellerBluffEdit?.scope===scope) {
      if(JSON.stringify(this._storytellerBluffEdit.roleIds)!==JSON.stringify(record?.roleIds||[])) {this._flushBluffDraft();return;}
      this._storytellerBluffEdit=null;
    }
    this._applyBluffDraft();
  }

  _applyBluffDraft() {
    if(this._isSpectator||this._storytellerBluffScope!==this._bluffDraftScope()||this._storytellerBluffEdit)return;
    this._applyingBluffDraft=true;
    try {
      this._store.commit('players/setBluff');
      (this._storytellerBluffRecord?.roleIds||[]).forEach((id,index)=>{
        const role=this._store.state.roles.get(id)||this._store.getters.rolesJSONbyId.get(id)||{};
        this._store.commit('players/setBluff',{index,role});
      });
    } finally {this._applyingBluffDraft=false;}
    this._store.commit('session/setBluffDraftStatus',{state:'saved',message:'说书人草稿已保存；不会自动发送给玩家。'});
  }

  _publishBluffs(seatIndexes,roleIds) {
    if(this._pendingBluff || this._isSpectator)return;
    const e=this._store.state.session.bluffEligibility;
    if(!e?.ok || !seatIndexes.length) {
      this._store.commit('session/setBluffDeliveryStatus',{state:'failed',message:!seatIndexes.length?'没有符合条件的目标座位。':'请先核对真实身份与候选提示。'});return;
    }
    let payload={gameId:e.gameId,revision:e.revision,seatRevision:e.seatRevision,baseVersions:e.baseVersions,
      seatIndexes,roleIds,operationId:this._newIdentityOperationId()};
    const sameRetry=this._retryBluff && JSON.stringify([this._retryBluff.gameId,this._retryBluff.roleIds,this._retryBluff.seatIndexes])===JSON.stringify([e.gameId,roleIds,seatIndexes]);
    if(sameRetry)payload=this._retryBluff;
    this._retryBluff=null;
    this._pendingBluff=payload;
    this._store.commit('session/setBluffDeliveryStatus',{state:'sending',message:'发送中，等待服务端保存确认…'});
    const failed=()=>{if(this._pendingBluff!==payload)return;this._pendingBluff=null;this._retryBluff=payload;
      this._store.commit('session/setBluffDeliveryStatus',{state:'failed',message:'未收到服务端确认；选择已保留，请恢复连接并重试。'});this.refreshBluffEligibility();};
    payload.timer=setTimeout(failed,8000);
    const {timer,...wire}=payload;
    if(!this._send('bluffPublish',wire)){clearTimeout(timer);failed();}
  }

  _receiveSeatBluffs(p) {
    if(!this._isSpectator || p?.roomId!==this._channel || p.gameId!==(this._store.state.session.voiceState.game?.id||'') ||
      p.playerId!==this._store.state.session.playerId || p.seatRevision<this._privateSeatRevision)return;
    const player=this._store.state.players.players[p.index];
    if(!player || !this.seatRecipientIds(player).includes(p.playerId))return;
    if(p.record && p.record.version<(this._receivedBluffVersion||0))return;
    this._receivedBluffVersion=p.record?.version||0;
    this._updateBluffs(p.record?.roleIds||[]);
  }

  sendGrimoire({ seatNumber, review = false } = {}) {
    if (this._isSpectator) return;
    if (review) {
      // 公开结果由服务器在结束时固定；客户端不能改写历史或提前发布草稿。
      this._send("review", {});
      return;
    }
    if (this._store.state.session.voiceState.game?.id && this._store.state.session.voiceState.game.allRecall) return;
    {
      const state = this._store.state;
      if (!state.session.voiceState.game?.id) { dialog.alert('请先开始游戏，再发送魔典。', '尚未发送'); return; }
      const frame = captureGrimoireFrame(this._store);
      if (!frame) { dialog.alert('魔典布局尚未完成，请稍后重试。', '尚未发送'); return; }
      this._send("grimoireSend", {
        gameId: state.session.voiceState.game?.id || "", seatRevision: this._seatRevision,
        seatIndex: Number(seatNumber) - 1, requestId: `${getConnectionId()}:${Date.now()}:${Math.random()}`,
        frame
      });
      return;
    }
  }

  _syncGrimoireAccess() {
    const state = this._store.state, gameId = state.session.voiceState.game?.id || "";
    const seat = this._isSpectator ? state.players.players.findIndex(p => this.seatRecipientIds(p).includes(state.session.playerId)) : -1;
    const key = `${this._channel}:${gameId}:${seat}:${this._isSpectator}`;
    if (key === this._grimoireAccessKey) return;
    this._grimoireAccessKey = key;
    this._store.commit("session/setReceivedGrimoire", null);
    if (this._socket?.readyState === WebSocket.OPEN) this._send("grimoireRead", {});
  }

  publishPostGameReview(enabled) {
    if (this._isSpectator) return;
    if (enabled) this.sendGrimoire({ review: true });
    this._send("reviewState", !!enabled);
  }

  sendChat(payload) {
    if (!this._store.state.session.sessionId) return;
    this._send("chat", {...payload,diagnosticId:payload.diagnosticId||this.diagnostics.beginChat()});
  }

  requestChatTarget(targetId) {
    if (!this._store.state.session.sessionId) return;
    this._store.commit("session/setChatTarget", "pending");
    this._send("resolveChat", { targetId, requestId: ++this._chatRouteRequestId });
  }

  updateChatGroup(payload) {
    if (!this._store.state.session.sessionId) return;
    this._send("chatGroup", payload);
  }

  sendQuickWake(payload) {
    if (!this._store.state.session.sessionId) return;
    this._send("quickWakeSend", payload);
  }

  acknowledgeQuickWake(payload) {
    if (!this._store.state.session.sessionId) return;
    this._send("quickWakeDisplayed", payload);
  }

  replyQuickWake(payload) {
    if (!this._store.state.session.sessionId) return;
    this._send("quickWakeReply", payload);
  }

  updateQuickWakePreset(payload) {
    if (!this._store.state.session.sessionId) return;
    this._send("quickWakePreset", payload);
  }

  sendInteraction(payload = {}) {
    if (!this._store.state.session.sessionId) return;
    if (!["rose", "tomato", "poop", "egg"].includes(payload.kind)) return;
    if (!payload.targetId) return;
    this._send("interaction", {
      kind: payload.kind,
      targetId: payload.targetId
    });
  }

  /**
   * 说书人修改房间级“白天文字私聊范围”。只发送请求，本地状态等服务端
   * 广播回来再更新，避免出现“本地已切换但服务端没生效”的假象。
   */
  setChatMode(mode) {
    if (this._isSpectator) return;
    if (!["allow_all", "deny_all", "neighbors_only"].includes(mode)) return;
    this._send("chatmode", { mode });
  }

  /**
   * Receive bluffs through a direct message. Only the demon is targeted.
   */
  _updateBluffs(bluffIds = []) {
    if (!this._isSpectator) return;
    this._store.commit("players/setBluff");
    bluffIds.forEach((roleId, index) => {
      const role =
        this._store.state.roles.get(roleId) ||
        this._store.getters.rolesJSONbyId.get(roleId);
      if (role) {
        this._store.commit("players/setBluff", { index, role });
      }
    });
  }

  _updateSharedGrimoire({ roleIds = [], alignments = [], reminders = [], roleDefinitions = {}, baseRoleIds } = {}, isReview) {
    if (!this._isSpectator) return;
    if (isReview && this._store.state.session.voiceState.game?.id) return;
    const generation = (this._reviewLoadGeneration || 0) + 1;
    this._reviewLoadGeneration = generation;
    this._reviewImageProbes?.forEach(cancel => cancel());
    this._reviewImageProbes = new Set();
    if (isReview) this._store.commit("session/setPostGameReview", true);
    this._store.state.players.players.forEach((player, index) => {
      const roleId = roleIds[index];
      const resolve = id => (isReview && Object.prototype.hasOwnProperty.call(roleDefinitions, id) ?
        trustedPromptRole(roleDefinitions[id], location.href, this._store.state.grimoire) : null) ||
        this._store.state.roles.get(id) || this._store.getters.rolesJSONbyId.get(id) || {};
      const targetRole = resolve(roleId);
      const baseId = isReview && Array.isArray(baseRoleIds) ? baseRoleIds[index] : roleId;
      // 身份不能依赖一次图片探测是否成功；图片回退由Token既有加载机制处理。
      const role = targetRole;
      /* 旧快照没有定义表时仍沿用本地角色资源。 */
      const legacyRole =
        this._store.state.roles.get(roleId) ||
        this._store.getters.rolesJSONbyId.get(roleId) ||
        {};
      // 整本魔典与公开复盘的角色、阵营是一份不可拆开的身份快照。
      // 旧包只有 roleIds，导致玩家能看到角色正面却没有红/蓝阵营环。
      // 兼容旧服务端/旧缓存：缺少 alignment 时按角色印刷阵营回退。
      const alignment = alignments[index] === "evil" || alignments[index] === "good"
        ? alignments[index]
        : ["minion", "demon"].includes(legacyRole.team) ? "evil" : "good";
      if (!isReview && Array.isArray(reminders[index])) {
        this._store.commit("players/update", {
          player,
          property: "reminders",
          value: reminders[index]
        });
      }
      // setIdentity 会替换座位对象；先把提示 Token 写入旧对象，再原子替换，
      // 这样展开后的新对象会同时保留提示 Token、角色与阵营。
      this._store.commit("players/setIdentity", { player, role, alignment });
      if (isReview) this._store.commit("players/update", { player: this._store.state.players.players[index],
        property: "reviewIdentityDescription", value: !roleId ? "真实身份未记录" :
          baseId && baseId !== roleId ? `真实身份：${targetRole.name || roleId}；展示身份：${resolve(baseId).name || baseId}` : `真实身份：${targetRole.name || roleId}` });
    });
    if (isReview) this._store.commit("session/setPostGameReview", true);
    else this._store.commit("session/setCanViewGrimoire", true);
  }

  /**
   * A player nomination. ST only
   * This also syncs the voting speed to the players.
   * Payload can be an object with {nomination} property or just the nomination itself, or undefined.
   * @param payload [nominator, nominee]|{nomination}
   */
  nomination(payload) {
    if (this._isSpectator) return;
    const nomination = payload ? payload.nomination || payload : payload;
    const players = this._store.state.players.players;
    // 提名与「取消提名」只允许发生在「白天提名」阶段。阶段之外的请求
    // 直接吞掉，房间就不会出现悬空的唱票画面。
    if (
      nomination &&
      this._store.state.session.voiceState.phase !== "nomination"
    )
      return;
    if (
      !nomination ||
      (players.length > nomination[0] && players.length > nomination[1])
    ) {
      this.setVotingSpeed(this._store.state.session.votingSpeed);
      this._send("nomination", {
        gameId: (this._store.state.session.voiceState.game || {}).id || "",
        nomination: nomination || false
      });
    }
  }

  /**
   * Set the isVoteInProgress status. ST only
   */
  setVoteInProgress() {
    if (this._isSpectator) return;
    this._send("isVoteInProgress", {
      gameId: (this._store.state.session.voiceState.game || {}).id || "",
      voteInstance: this._store.state.session.voteInstance,
      value: this._store.state.session.isVoteInProgress
    });
  }

  /**
   * Publish the storyteller-controlled discussion timer.
   */
  setDiscussionTimer(timer) {
    if (this._isSpectator) return;
    // 只回传合法的计时器对象：一个缺失/空的本地快照不应该被广播成
    // `["timer", null]`，否则所有玩家页面都会在设置计时器时抛错。
    if (!timer || typeof timer !== "object") return;
    this._send("timer", { ...timer, gameId: this._store.state.session.voiceState.game?.id || "",
      baseRevision: this._store.state.session.discussionTimer.revision || 0 });
  }

  /**
   * Send the isNight status. ST only
   */
  setIsNight() {
    if (this._isSpectator) return;
    this._send("isNight", this._store.state.grimoire.isNight);
  }

  /**
   * Send the isVoteHistoryAllowed state. ST only
   */
  setVoteHistoryAllowed() {
    if (this._isSpectator) return;
    this._send(
      "isVoteHistoryAllowed",
      this._store.state.session.isVoteHistoryAllowed
    );
  }

  requestVoteHistory() {
    this._send("voteHistoryRequest", {
      gameId: (this._store.state.session.voiceState.game || {}).id || ""
    });
  }

  finalizeVote(type = "Execution") {
    if (this._isSpectator) return;
    this._send("finalizeVote", {
      gameId: (this._store.state.session.voiceState.game || {}).id || "",
      voteInstance: this._store.state.session.voteInstance,
      type: type === "Exile" ? "Exile" : "Execution"
    });
  }

  restartVote() {
    if (this._isSpectator) return;
    this._send("restartVote", {
      gameId: (this._store.state.session.voiceState.game || {}).id || "",
      voteInstance: this._store.state.session.voteInstance
    });
  }

  /**
   * 匿名投票开关（说书人手动主持工具）。本地立刻切换（服务端会回显给其它
   * 客户端），并同步给服务器；不自动绑定任何角色 ID。
   */
  setAnonymousVote(enabled) {
    if (this._isSpectator) return;
    const value = enabled === true;
    this._store.commit("session/setAnonymousVote", value);
    this._send("anonymousVote", value);
  }

  /**
   * Send the voting speed. ST only
   * @param votingSpeed voting speed in seconds, minimum 1
   */
  setVotingSpeed(votingSpeed) {
    if (this._isSpectator) return;
    if (votingSpeed) {
      this._send("votingSpeed", {
        gameId: (this._store.state.session.voiceState.game || {}).id || "",
        value: votingSpeed
      });
    }
  }

  /**
   * Set which player is on the block. ST only
   * @param playerIndex, player id or -1 for empty
   */
  setMarked(playerIndex) {
    if (this._isSpectator) return;
    // 只有真正的座位下标才会回传。快照缺字段时 markedPlayer 可能是
    // undefined，直接发出去会变成 `["marked", null]`，服务器
    // `Number(null) === 0` 会把它当作“1 号位待处决”。
    if (!Number.isInteger(playerIndex)) return;
    this._send("marked", {
      gameId: (this._store.state.session.voiceState.game || {}).id || "",
      value: playerIndex
    });
  }

  /**
   * Clear the vote history for everyone. ST only
   */
  clearVoteHistory() {
    if (this._isSpectator) return;
    this._send("clearVoteHistory");
  }

  /**
   * Send a vote. Player or ST
   * @param index Seat of the player
   * @param sync Flag whether to sync this vote with others or not
   */
  vote([index]) {
    const player = this._store.state.players.players[index];
    if (!player) return;
    if (
      this._store.state.session.playerId === player.id ||
      (player.coPlayMembers || []).some(member => member && member.id === this._store.state.session.playerId) ||
      !this._isSpectator
    ) {
      // send vote only if it is your own vote or you are the storyteller
      this._send("vote", {
        gameId: (this._store.state.session.voiceState.game || {}).id || "",
        voteInstance: this._store.state.session.voteInstance,
        index,
        vote: this._store.state.session.votes[index],
        fromST: !this._isSpectator
      });
    }
  }

  /**
   * Handle an incoming vote, but only if it is from ST or unlocked.
   * @param index
   * @param vote
   * @param fromST
   */
  _handleVote([index, vote, fromST] = []) {
    const { session, players } = this._store.state;
    const playerCount = players.players.length;
    const indexAdjusted =
      (index - 1 + playerCount - session.nomination[1]) % playerCount;
    if (fromST || indexAdjusted >= session.lockedVote - 1) {
      this._store.commit("session/vote", [index, vote]);
    }
  }

  /**
   * Lock a vote. ST only
   */
  lockVote() {
    if (this._isSpectator) return;
    const { lockedVote, votes, nomination } = this._store.state.session;
    const { players } = this._store.state.players;
    const index = (nomination[1] + lockedVote - 1) % players.length;
    this._send("lock", {
      gameId: (this._store.state.session.voiceState.game || {}).id || "",
      voteInstance: this._store.state.session.voteInstance,
      lock: this._store.state.session.lockedVote,
      vote: votes[index]
    });
  }

  /**
   * Update vote lock and the locked vote, if it differs. Player only
   * @param lock
   * @param vote
   * @private
   */
  _handleLock([lock, vote] = []) {
    if (!this._isSpectator) return;
    this._store.commit("session/lockVote", lock);
    if (lock > 1) {
      const { lockedVote, nomination } = this._store.state.session;
      const { players } = this._store.state.players;
      const index = (nomination[1] + lockedVote - 1) % players.length;
      if (this._store.state.session.votes[index] !== vote) {
        this._store.commit("session/vote", [index, vote]);
      }
    }
  }

  /**
   * Swap two player seats. ST only
   * @param payload
   */
  swapPlayer(payload) {
    if (this._isSpectator) return;
    this._send("swap", payload);
  }

  /**
   * Move a player to another seat. ST only
   * @param payload
   */
  movePlayer(payload) {
    if (this._isSpectator) return;
    this._send("move", payload);
  }

  /**
   * 说书人「随机座位」：把一次整体重排的 permutation 交给服务器做权威重排
   * （order[新下标] = 旧下标）。服务端会用与 swap/move 相同的私密档案迁移
   * 语义把 Delivered / Draft / 说书人 Token 一起搬走，并把新的公开座位顺序
   * 广播给所有客户端，因此刷新/重连不会再退回旧顺序。
   */
  reorderSeats(permutation) {
    if (this._isSpectator) return;
    if (!Array.isArray(permutation) || permutation.length < 2) return;
    // 本页的 Delivered / 发送中簿记同样按座位下标存：整表重排后必须一起搬，
    // 否则红绿点与 Delivered tooltip 会指向错的人（服务端档案已经搬过了）。
    this._store.commit("session/reindexStorytellerIdentities", permutation);
    this._send("reorder", permutation);
  }

  /**
   * Remove a player. ST only
   * @param payload
   */
  removePlayer(payload) {
    if (this._isSpectator && !this._store.state.session.isOwner) return;
    this._send("remove", payload);
  }

  /**
   * Remove the entire table. Unlike kicking a player, this also clears the
   * server's private identity snapshots so a newly built table never revives
   * roles/alignment from matching old seat indexes.
   */
  clearAllSeats() {
    // The visible storyteller panel is the user-facing authority. During a
    // reconnect its old socket flag may still say spectator, but that must
    // not swallow a deliberate full-table reset before it reaches server.
    if (
      this._isSpectator &&
      this._store.state.session.isSpectator &&
      !this._store.state.session.isOwner
    )
      return;
    this._send("clearSeats");
    // “待处决”的骷髅头（markedPlayer）与座位档案是两套状态，清空座位时
    // 必须一起清掉，否则新一局仍会显示上一局的处决标记。
    this._store.commit("session/clearMarkedPlayer");
    this._store.commit("session/nomination");
  }
}

export default store => {
  // setup
  const session = new LiveSession(store);
  // Components use this non-reactive service only for acknowledged commands,
  // never as a second game-state store.
  store.$liveSession = session;

  // listen to mutations
  store.subscribe(({ type, payload }, state) => {
    if(['setEdition','setCustomRoles'].includes(type))Promise.resolve().then(()=>session._applyBluffDraft());
    if (["session/setVoiceState", "players/set", "session/leaveRoom", "session/setSpectator"].includes(type))
      Promise.resolve().then(() => session._syncGrimoireAccess());
    // Mutations committed while restoring an authoritative server `gs` packet
    // only update the local UI. Sending them back here used to fan one packet
    // out into isNight/timer/bootlegger/edition/fabled/etc., which the server
    // broadcast again and could eventually trip the anti-spam limit.
    if (session._isApplyingRemoteGamestate) return;
    if (session._isApplyingRemoteVoteState) return;
    switch (type) {
      case "session/setSessionId":
        if (state.session.sessionId) {
          session.connect(state.session.sessionId);
        } else {
          window.location.hash = "";
          session.disconnect();
        }
        break;
      case "session/claimSeat":
        session.claimSeat(payload);
        break;
      case "session/claimStoryteller":
        session.claimStoryteller();
        break;
      case "session/releaseStoryteller":
        session.releaseStoryteller();
        break;
      case "session/kickPlayer":
        session.kickPlayer(payload);
        break;
      case "session/clearAllSeats":
        session.clearAllSeats();
        break;
      case "session/disbandRoom":
        session.disbandRoom();
        break;
      case "session/distributeRoles":
        if (payload) {
          // The visible “发送角色” button is an intentional re-send action.
          // It must work even after a prior connection interruption.
          session.distributeRoles(true);
        }
        break;
      case "session/sendPlayerIdentity":
        session.sendPlayerIdentity(payload);
        break;
      case "session/requestCoPlay":
        session.requestCoPlay(payload);
        break;
      case "session/kickSeat":
        session.kickSeat(payload);
        break;
      case "session/requestVoiceAction":
        session.requestVoiceAction(payload);
        break;
      case "session/distributeRoleTypes":
        session.distributeRoleTypes();
        break;
      case "session/setBootlegger":
        session.setBootlegger(payload);
        store.commit("players/applyBootlegger", payload);
        break;
      case "session/setUseOldOrder":
        session.setUseOldOrder(payload);
        break;
      case "session/setUseOldRole":
        session.setUseOldRole(payload);
        break;
      case "session/leaveRoom":
        session._resetBluffDraft();
        // 任何退出房间的路径（主动离开 / 解散 / 超时 / 代际失效 / 返回主页）
        // 都让说书人恢复意图作废：A 房间的意图不可能带进 B 房间。
        session._clearStorytellerIntent();
        session._restoringStoryteller = false;
        clearTimeout(session._storytellerRestoreTimer);
        session._storytellerRestoreTimer = null;
        session._spectatorFallbackDone = false;
        window.location.hash = "";
        session.disconnect();
        store.commit("players/clear");
        if (state.grimoire.isNight) store.commit("toggleNight");
        if (!state.grimoire.isPublic) store.commit("toggleGrimoire");
        store.commit("toggleModal");
        break;
      case "session/requestLeaveRoom":
        session.requestLeaveRoom();
        break;
      case "session/cacheSeatIdentity":
        session.cacheSeatIdentity(payload);
        break;
      case "session/distributeBluffs":
        session.distributeBluffs();
        break;
      case "players/setBluff":
        session.saveStorytellerBluffs();
        break;
      case "session/distributeBluffsToSeat":
        session.distributeBluffsToSeat(payload);
        break;
      case "session/sendChat":
        session.sendChat(payload);
        break;
      case "session/requestChatTarget":
        session.requestChatTarget(payload);
        break;
      case "session/setChatTarget":
        session._chatRouteRequestId += 1;
        break;
      case "session/markChatRead":
        session._send("chatRead", { gameId: state.session.voiceState.game?.id || "", ids: payload });
        break;
      case "session/requestGrimoire":
        session._send("grimoireRead", {});
        break;
      case "session/requestDiscussionTimer":
        session.setDiscussionTimer(payload);
        break;
      case "session/requestTimerState":
        session._send("timerRead", {});
        break;
      case "session/requestInteraction":
        session.sendInteraction(payload);
        break;
      case "session/updateChatGroup":
        session.updateChatGroup(payload);
        break;
      case "session/sendQuickWake":
        session.sendQuickWake(payload);
        break;
      case "session/acknowledgeQuickWake":
        session.acknowledgeQuickWake(payload);
        break;
      case "session/replyQuickWake":
        session.replyQuickWake(payload);
        break;
      case "session/updateQuickWakePreset":
        session.updateQuickWakePreset(payload);
        break;
      case "session/requestChatMode":
        session.setChatMode(payload && payload.mode);
        break;
      case "session/requestAnonymousVote":
        session.setAnonymousVote(payload && payload.enabled);
        break;
      case "session/sendGrimoire":
        session.sendGrimoire(payload);
        break;
      case "session/publishPostGameReview":
        session.publishPostGameReview(payload);
        break;
      case "session/nomination":
      case "session/setNomination":
        session.nomination(payload);
        break;
      case "session/setVoteInProgress":
        session.setVoteInProgress(payload);
        break;
      case "session/setDiscussionTimer":
        session.setDiscussionTimer(payload);
        break;
      case "session/voteSync":
        session.vote(payload);
        break;
      case "session/lockVote":
        session.lockVote();
        break;
      case "session/finalizeVote":
        session.finalizeVote(payload);
        break;
      case "session/restartVote":
        session.restartVote();
        break;
      case "session/setVotingSpeed":
        session.setVotingSpeed(payload);
        break;
      case "session/clearVoteHistory":
        session.clearVoteHistory();
        break;
      case "session/setPendingDayResolution":
      case "session/clearPendingDayResolution":
      case "session/resolveDay":
        session.sendGamestate();
        break;
      case "session/setVoteHistoryAllowed":
        session.setVoteHistoryAllowed();
        break;
      case "session/requestVoteHistory":
        session.requestVoteHistory();
        break;
      case "toggleNight":
      case "setNight":
        session.setIsNight();
        break;
      case "setEdition":
      case "selectEdition":
        session.sendEdition();
        // 后台剧本自动加入的传奇角色也立即走现有公开传奇同步链路。
        session.sendFabled();
        break;
      case "players/setFabled":
        store.commit("players/applyBootlegger", state.session.bootlegger);
        session.sendFabled();
        break;
      case "session/setMarkedPlayer":
        session.setMarked(payload);
        break;
      case "players/swap":
        session.swapPlayer(payload);
        break;
      case "players/move":
        session.movePlayer(payload);
        break;
      case "players/reorder":
        // 随机座位必须落到服务器权威布局上，不能只改本地顺序。
        session.reorderSeats(payload);
        break;
      case "players/remove":
        session.removePlayer(payload);
        break;
      case "players/set":
      case "players/clear":
      case "players/add":
      case "players/addSeats":
        if (session._isApplyingRemoteSeatLayout) break;
        session.syncSeatCount();
        // 座位数变化后，把服务器补发的说书人提示标记重新落到对应座位。
        session._reapplyReminderNotes();
        break;
      case "players/update":
        if (session._isApplyingRemoteSeatLayout) break;
        if (session._isApplyingRemoteGhostVote) break;
        if (payload.property === "reminders") {
          // 说书人私人提示标记：写入服务器档案，换设备/刷新后可恢复。
          session.publishReminders();
        } else if (payload.property === "pronouns") {
          session.sendPlayerPronouns(payload);
        } else {
          session.sendPlayer(payload);
        }
        break;
    }
  });

  // check for session Id in hash
  const sessionId = window.location.hash.substr(1);
  if (sessionId) {
    // 刷新进入房间时，原先只强制 setSpectator(true)、完全没有恢复 isOwner：
    // 房主本人（连接角色 owner-storyteller）刷新后不仅丢了说书人席，本地 owner
    // 标记还会被紧接着的存档写回覆盖成 false。这里只对「本地存档里 sessionId
    // 与当前 hash 房号一致」的情况做最小恢复；owner 权限仍然由服务端的保留
    // 角色 + 同 clientId 校验裁决：换设备即使带着复制的 localStorage 也会被
    // 服务端拒绝，然后退回旁观并清掉本地 owner 标记。
    let savedSession = null;
    try {
      const rawSession = localStorage.getItem("session");
      if (rawSession) {
        const [spectator, savedSessionId, isOwner = false] = JSON.parse(rawSession);
        if (savedSessionId === sessionId) {
          savedSession = { spectator, isOwner };
        }
      }
    } catch (error) {
      savedSession = null;
    }
    store.commit("session/setOwner", !!(savedSession && savedSession.isOwner));
    store.commit("session/setSpectator", true);
    store.commit("session/setSessionId", sessionId);
    store.commit("toggleGrimoire", false);
  }
};
